import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { writeBatch, doc, collection, limit, orderBy, startAfter, onSnapshot, query, where, getDocs, updateDoc, setDoc, getDoc } from 'firebase/firestore';
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, Cell } from 'recharts';
import { 
    ChevronRight, School, Users, Wallet, User, Stethoscope, List, PieChart as PieChartIcon, Loader2, Printer, 
    Download, Activity, AlertTriangle, Target, Compass, ShieldCheck, Briefcase, 
    GraduationCap, Smartphone, BrainCircuit, ShieldAlert, HeartPulse, Brain, Home, 
    BarChart3, Sparkles, TrendingUp, CheckCircle 
} from 'lucide-react';
import * as XLSX from 'xlsx';
import EditableAIBox from './EditableAIBox';
import { forms as NEW_FORMS } from '../data/forms';

// Motorlar
import { calculateStudentInfo } from '../utils/StudentInfo_engine';
import { calculateSociometry } from '../utils/Sociometry_engine';
import { calculateRibaClassASP, calculateRibaSchoolASP } from '../utils/Riba_engine';

// Rapor UI'ları
import { BasarisizlikKurumsalUI } from './reports/BasarisizlikReports';
import { YasamPenceremKurumsalUI } from './reports/YasamPenceremReports';
import { MeslekiDegerlerKurumsalUI } from './reports/MeslekiDegerlerReports';
import { DevamsizlikKurumsalUI } from './reports/DevamsizlikReports';
import { SinavKaygisiKurumsalUI } from './reports/SinavKaygisiReports';
import { InternetKurumsalUI } from './reports/InternetBagimliligiReports';
import { Dass21KurumsalUI } from './reports/Dass21Reports';
import { RosenbergKurumsalUI } from './reports/RosenbergReports';
import { HollandKurumsalUI } from './reports/HollandReports';
import { VakKurumsalUI } from './reports/VakReports';
import { CokluZekaKurumsalUI } from './reports/CokluZekaReports';
import { SosyometriKurumsalUI } from './reports/SosyometriReports';
import { StudentInfoKurumsalUI } from './reports/StudentInfoReports';
import { PsikolojikSaglamlikBulkReportUI } from './reports/psikolojikSaglamlikReports';
import { LiebowitzBulkReportUI } from './reports/liebowitzReports';

const AI_API_URL = "https://generateanalysis-yyf3isw5zq-uc.a.run.app";
const CRITICAL_ITEMS_IDS = [3, 7, 9, 11, 14, 16, 18, 25, 33, 36];

const AVAILABLE_TESTS = NEW_FORMS.map(form => ({
  id: form.id, name: form.title, category: 'Psikolojik Değerlendirme',
  description: form.description, type: form.type, options: form.options, questions: form.items, intro: form.intro,
  reference: form.reference
}));

const PrintStyles = () => (<style>{`
  @media print { 
    @page { size: A4 portrait; margin: 10mm; } 
    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; background-color: white !important; }
    .no-print { display: none !important; }
    .avoid-break { page-break-inside: avoid !important; break-inside: avoid !important; }
    .bg-slate-900 { background-color: #0f172a !important; color: white !important; }
  }
`}</style>);

// --- YENİ BİLEŞEN: ÖĞRENCİ BİLGİ FORMU DASHBOARD ---
const StudentInfoDashboard = ({ submissions, filterClass, allClasses = [] }) => {
  const [activeTab, setActiveTab] = useState('aile');
  
  // İsim Bulucu Yardımcı Fonksiyon
  const getStudentLabel = (no, className) => {
    if (!allClasses || allClasses.length === 0) return no;
    // İlgili sınıfı bul
    const foundClass = allClasses.find(c => c.name === className);
    if (!foundClass) return no;
    // Öğrenciyi bul
    const foundStudent = foundClass.students.find(s => s.no.toString() === no.toString());
    return foundStudent ? `${no} - ${foundStudent.name}` : no;
  };

  const aggregateData = () => {
    const data = {
      aile: { vefat: [], ayri: [], cokKardes: [] },
      ekonomi: { dusukGelir: [], calisan: [] },
      saglik: { kronik: [], ilac: [], kaza: [] }
    };

    submissions.forEach(sub => {
      if (filterClass && sub.studentClass !== filterClass) return;
      const results = calculateStudentInfo(sub.answers);
      
      results.risks.forEach(risk => {
        // Burada ismi buluyoruz
        const displayName = getStudentLabel(sub.studentNo, sub.studentClass);
        const studentInfo = { label: displayName, class: sub.studentClass, detail: risk.text };
        
        if (risk.text.includes('Vefat')) data.aile.vefat.push(studentInfo);
        if (risk.text.includes('Ayrı')) data.aile.ayri.push(studentInfo);
        if (risk.text.includes('Çok Kardeşli')) data.aile.cokKardes.push(studentInfo);
        if (risk.text.includes('Düşük Gelir')) data.ekonomi.dusukGelir.push(studentInfo);
        if (risk.text.includes('Öğrenci Çalışıyor')) data.ekonomi.calisan.push(studentInfo);
        if (risk.text.includes('Kronik Hastalık')) data.saglik.kronik.push(studentInfo);
        if (risk.text.includes('Sürekli İlaç')) data.saglik.ilac.push(studentInfo);
        if (risk.text.includes('Geçmiş Kaza')) data.saglik.kaza.push(studentInfo);
      });
    });
    return data;
  };

  const aggData = aggregateData();

  const RiskList = ({ title, items, icon: Icon, color }) => (
    <div className={`bg-white p-5 rounded-2xl border border-slate-200 shadow-sm mb-4`}>
      <div className="flex justify-between items-center mb-4">
        <h4 className={`font-bold text-sm uppercase flex items-center ${color}`}>
          <Icon className="h-4 w-4 mr-2"/> {title}
        </h4>
        <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded text-xs font-black">{items.length} Öğrenci</span>
      </div>
      {items.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {items.map((stu, i) => (
            <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-xs border border-slate-100">
              <div>
                <span className="font-bold text-slate-800 mr-2">{stu.label}</span>
                <span className="text-[10px] text-slate-400">({stu.class})</span>
                {stu.detail.includes(':') && <span className="block text-[10px] text-slate-500 mt-0.5">{stu.detail.split(':')[1]}</span>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-slate-400 italic">Kayıt bulunamadı.</p>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex gap-2 border-b border-slate-200 pb-1 no-print overflow-x-auto">
        <button onClick={() => setActiveTab('aile')} className={`px-6 py-3 rounded-t-xl font-bold text-sm transition flex items-center ${activeTab === 'aile' ? 'bg-white border-x border-t border-slate-200 text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}><Home className="h-4 w-4 mr-2"/> Aile Yapısı</button>
        <button onClick={() => setActiveTab('ekonomi')} className={`px-6 py-3 rounded-t-xl font-bold text-sm transition flex items-center ${activeTab === 'ekonomi' ? 'bg-white border-x border-t border-slate-200 text-green-600' : 'text-slate-400 hover:text-slate-600'}`}><Wallet className="h-4 w-4 mr-2"/> Ekonomik Durum</button>
        <button onClick={() => setActiveTab('saglik')} className={`px-6 py-3 rounded-t-xl font-bold text-sm transition flex items-center ${activeTab === 'saglik' ? 'bg-white border-x border-t border-slate-200 text-red-600' : 'text-slate-400 hover:text-slate-600'}`}><HeartPulse className="h-4 w-4 mr-2"/> Sağlık & Özel</button>
      </div>
      <div className="bg-white rounded-b-2xl min-h-[400px]">
        {activeTab === 'aile' && (
          <div className="animate-in fade-in zoom-in-95 duration-300">
            <RiskList title="Ebeveyn Kaybı (Vefat)" items={aggData.aile.vefat} icon={User} color="text-slate-800" />
            <RiskList title="Parçalanmış Aile (Boşanmış/Ayrı)" items={aggData.aile.ayri} icon={Users} color="text-amber-600" />
            <RiskList title="Kalabalık Aile (5+ Kardeş)" items={aggData.aile.cokKardes} icon={Home} color="text-blue-600" />
          </div>
        )}
        {activeTab === 'ekonomi' && (
          <div className="animate-in fade-in zoom-in-95 duration-300">
            <RiskList title="Düşük Gelir Beyanı" items={aggData.ekonomi.dusukGelir} icon={Wallet} color="text-red-600" />
            <RiskList title="Çalışan Öğrenciler" items={aggData.ekonomi.calisan} icon={Briefcase} color="text-orange-600" />
          </div>
        )}
        {activeTab === 'saglik' && (
          <div className="animate-in fade-in zoom-in-95 duration-300">
            <RiskList title="Kronik Rahatsızlıklar" items={aggData.saglik.kronik} icon={HeartPulse} color="text-rose-600" />
            <RiskList title="Sürekli İlaç Kullananlar" items={aggData.saglik.ilac} icon={Stethoscope} color="text-blue-600" />
            <RiskList title="Geçmiş Kaza/Ameliyat" items={aggData.saglik.kaza} icon={Activity} color="text-slate-600" />
          </div>
        )}
      </div>
    </div>
  );
};

const InstitutionalAnalysis = ({
  submissions,
  user,
  academicYear = '2026-2027'
}) => {
 
  const [activeTab, setActiveTab] = useState('school'); 
  const [selectedTest, setSelectedTest] = useState(''); 
  const [selectedClass, setSelectedClass] = useState(null); 
  const [downloading, setDownloading] = useState(false); 
  const [selectedItemForAnalysis, setSelectedItemForAnalysis] = useState(null); 
  const [aiResult, setAiResult] = useState(null); 
  const [aiLoading, setAiLoading] = useState(false);
const [mockSubmissions, setMockSubmissions] = useState(null);
const [mockStudentInfoRoster, setMockStudentInfoRoster] = useState(null);  

  const [allClasses, setAllClasses] = useState([]); 
  // Eğitim yılı değiştiğinde önceki dönemin ekran seçimlerini temizle
useEffect(() => {
  setSelectedTest('');
  setSelectedClass(null);
  setAiResult(null);
}, [academicYear]);

// Kayıtlı kurumsal AI analizini eğitim yılına göre getir
useEffect(() => {

  const fetchSavedCorporateAI = async () => {

    if (!user?.uid || !selectedTest) {
      setAiResult(null);
      return;
    }

    // Yıl/test/sınıf değiştiğinde eski raporu ekrandan hemen temizle
    setAiResult(null);

    const scopeKey = selectedClass ? selectedClass : 'school';

    // YENİ FORMAT:
    // UID_2026-2027_TEST_SINIF
    const safeDocId =
      `${user.uid}_${academicYear}_${selectedTest}_${scopeKey}`
        .replace(/[\/\s]/g, '_');

    try {

      let docRef = doc(
        db,
        "corporate_analyses",
        safeDocId
      );

      let docSnap = await getDoc(docRef);

      // GERİYE UYUMLULUK:
      // Geçen yıl oluşturulmuş eski corporate_analyses kayıtlarında
      // academicYear belge ID'sinde bulunmuyordu.
      if (!docSnap.exists() && academicYear === '2025-2026') {

        const legacyDocId =
          `${user.uid}_${selectedTest}_${scopeKey}`
            .replace(/[\/\s]/g, '_');

        docRef = doc(
          db,
          "corporate_analyses",
          legacyDocId
        );

        docSnap = await getDoc(docRef);
      }

      if (
        docSnap.exists() &&
        docSnap.data().aiAnalysis
      ) {
        setAiResult(docSnap.data().aiAnalysis);
      }

    } catch (e) {
      console.error(
        "Kayıtlı kurumsal analiz çekilemedi:",
        e
      );
    }
  };

  fetchSavedCorporateAI();

}, [
  selectedTest,
  selectedClass,
  user?.uid,
  academicYear
]);

const [schoolType, setSchoolType] = useState(() => {
    return localStorage.getItem('rehber360_schoolType') || 'Anadolu / Genel Lise';
  });

  useEffect(() => {
    localStorage.setItem('rehber360_schoolType', schoolType);
  }, [schoolType]);

  // YENİ EKLENEN KISIM: Sınıfları sadece senin uzman koduna (7335) göre çeken akıllı süzgeç
  useEffect(() => {

  if (!user?.uid) return;

  const q = query(
    collection(db, "classes"),
    where("clinicianUid", "==", user.uid)
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {

    const myClasses = snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter(cls => {

        // Eğitim yılı özelliğinden önceki sınıflar
        // 2025-2026 kabul edilir.
        const classYear =
          cls.academicYear || '2025-2026';

        return classYear === academicYear;
      });

    setAllClasses(myClasses);
  });

  return () => unsubscribe();

}, [user?.uid, academicYear]);

  
  // YENİ EKLENEN: Menünün simülatör verilerini (Sosyometri dahil) görebilmesi için düzeltildi
  const activeSubmissionsForMenu = mockSubmissions || submissions;
  const availableTests = [...new Set(activeSubmissionsForMenu.filter(s => s.status === 'reported').map(s => s.testId))]; 
  const testOptions = AVAILABLE_TESTS.filter(t => availableTests.includes(t.id));

  // ============================================================
// ÖĞRENCİ KİMLİĞİ / GÜNCEL SINIF RESOLVER
// ============================================================

const normalizeInstitutionalClass =
  value =>
    String(
      value ?? ''
    )
      .trim()
      .toUpperCase();


const normalizeInstitutionalStudentNo =
  value =>
    String(
      value ?? ''
    ).trim();


const currentStudentIndex =
  useMemo(() => {

    const map =
      new Map();


    if (
      !Array.isArray(allClasses)
    ) {
      return map;
    }


    allClasses.forEach(
      classItem => {

        const className =
          String(
            classItem?.name ?? ''
          ).trim();


        const students =
          Array.isArray(
            classItem?.students
          )
            ? classItem.students
            : [];


        students.forEach(
          student => {

            const studentNo =
              normalizeInstitutionalStudentNo(
                student?.no ??
                student?.number
              );


            if (!studentNo) {
              return;
            }


            map.set(
              studentNo,
              {
                className,

                name:
                  String(
                    student?.name ?? ''
                  ).trim(),

                student
              }
            );
          }
        );
      }
    );


    return map;

  }, [allClasses]);


const getCurrentStudentLocation =
  no => {

    const studentNo =
      normalizeInstitutionalStudentNo(
        no
      );


    if (!studentNo) {
      return null;
    }


    return (
      currentStudentIndex.get(
        studentNo
      ) ||
      null
    );
  };


// Sosyometri ve RİBA uygulandığı dönemin
// sınıf/grup yapısını ölçtüğü için tarihsel kalır.
const usesHistoricalClassSemantics =
  testId => {

    const normalizedTestId =
      String(
        testId ?? ''
      );


    return (
      normalizedTestId ===
        'sosyometri_testi' ||

      normalizedTestId ===
        'sociometry' ||

      normalizedTestId
        .startsWith(
          'riba_kurumsal'
        )
    );
  };


const getSubmissionClassForReport =
  (
    sub,
    testId = selectedTest
  ) => {

    const historicalClass =
      String(
        sub?.studentClass ?? ''
      ).trim();


    if (
      usesHistoricalClassSemantics(
        testId
      )
    ) {
      return historicalClass;
    }


    // Simülatörün kendi sınıf verisini koru.
    if (mockSubmissions) {
      return historicalClass;
    }


    // Sınıf listesi henüz yüklenmediyse
    // mevcut davranış geçici olarak korunsun.
    if (
      !Array.isArray(allClasses) ||
      allClasses.length === 0
    ) {
      return historicalClass;
    }


    const currentLocation =
      getCurrentStudentLocation(
        sub?.studentNo ??
        sub?.answers?.[302]
      );


    // Güncel okul listesinde artık yoksa
    // aktif sınıf raporuna dahil edilmez.
    return (
      currentLocation
        ?.className ||
      null
    );
  };


const isSubmissionInClassForReport =
  (
    sub,
    className,
    testId = selectedTest
  ) => {

    if (!className) {
      return true;
    }


    const resolvedClass =
      getSubmissionClassForReport(
        sub,
        testId
      );


    return (
      normalizeInstitutionalClass(
        resolvedClass
      ) ===
      normalizeInstitutionalClass(
        className
      )
    );
  };


const getSubmissionDisplayClass =
  sub => {

    const currentLocation =
      getCurrentStudentLocation(
        sub?.studentNo ??
        sub?.answers?.[302]
      );


    return (
      currentLocation
        ?.className ||

      sub?.studentClass ||

      'Sınıfsız'
    );
  };


const getStudentLabel =
  (no, className) => {

    const currentLocation =
      getCurrentStudentLocation(
        no
      );


    if (
      !mockSubmissions &&
      currentLocation?.name
    ) {
      return (
        `${currentLocation.name} (${no})`
      );
    }


    const targetClass =
      normalizeInstitutionalClass(
        className
      );


    const targetNo =
      normalizeInstitutionalStudentNo(
        no
      );


    const foundClass =
      allClasses.find(
        c =>
          normalizeInstitutionalClass(
            c?.name
          ) ===
          targetClass
      );


    const foundStudent =
      (
        foundClass?.students ||
        []
      ).find(
        student =>
          normalizeInstitutionalStudentNo(
            student?.no ??
            student?.number
          ) ===
          targetNo
      );


    return foundStudent
      ? `${foundStudent.name} (${no})`
      : String(no);
  };


// Öğrenci Bilgi Formu katılım oranı için gerçek kayıtlı öğrenci sayısını hesaplar
const getRegisteredStudentCount = (className = null) => {

  // 🧪 TEST MODU:
  // Gerçek Firebase sınıflarına kesinlikle dokunma.
  if (mockSubmissions && mockStudentInfoRoster) {
    if (!className) {
      return mockStudentInfoRoster.students.length;
    }

    const requestedClass = String(className || '').trim().toUpperCase();
    const simulatorClass = String(mockStudentInfoRoster.name || '').trim().toUpperCase();

    return requestedClass === simulatorClass
      ? mockStudentInfoRoster.students.length
      : 0;
  }

  // GERÇEK MOD
  if (!allClasses || allClasses.length === 0) return 0;

  const normalizeClass = (value) =>
    String(value || '').trim().toUpperCase();

  const targetClass = className
    ? normalizeClass(className)
    : null;

  const relevantClasses = targetClass
    ? allClasses.filter(c => normalizeClass(c.name) === targetClass)
    : allClasses;

   const uniqueStudents = new Set();

  relevantClasses.forEach(cls => {

    const normalizedClassName =
      normalizeClass(cls.name);

    (cls.students || []).forEach(student => {

      const no =
        String(
          student.no ??
          student.number ??
          ''
        ).trim();

      if (!no) return;

      uniqueStudents.add(
        `${normalizedClassName}_${no}`
      );
    });
  });

  return uniqueStudents.size;
};

  const handlePrint = (areaId) => {

  setDownloading(true);


  const printArea =
    document.getElementById(
      areaId
    );


  if (!printArea) {

    setDownloading(false);

    return;
  }


  const content =
    printArea.innerHTML;


  // Öğrenci Bilgi Formu Katılım Takibi
  // diğer raporlardan farklı olarak yatay basılacak.
  const isParticipationPrint =
    areaId ===
    'studentinfo-participation-area';


  const iframe =
    document.createElement(
      'iframe'
    );


  // ---------------------------------------------------------
  // MOBİL SAFARİ / CHROME KORUMASI
  // ---------------------------------------------------------

  iframe.style.position =
    'fixed';

  iframe.style.bottom =
    '0';

  iframe.style.right =
    '0';

  iframe.style.width =
    isParticipationPrint
      ? '1400px'
      : '1024px';

  iframe.style.height =
    '100vh';

  iframe.style.opacity =
    '0.01';

  iframe.style.pointerEvents =
    'none';

  iframe.style.zIndex =
    '-1';


  document.body.appendChild(
    iframe
  );


  const doc =
    iframe.contentWindow.document;


  // Mevcut Tailwind / uygulama CSS'lerini taşı.
  let stylesHTML = '';


  document
    .querySelectorAll(
      'style, link[rel="stylesheet"]'
    )
    .forEach(el => {

      stylesHTML +=
        el.outerHTML;

    });


  const pageSize =
    isParticipationPrint
      ? 'A4 landscape'
      : 'A4 portrait';


  const pageMargin =
    isParticipationPrint
      ? '7mm'
      : '10mm';


  const participationPrintStyles =
    isParticipationPrint

      ? `

        /* ============================================= */
        /* ÖĞRENCİ BİLGİ FORMU KATILIM TAKİBİ — PRINT */
        /* ============================================= */

        .participation-report-header {
          padding: 7px 10px !important;
        }


        .participation-report-header h3 {
          font-size: 13px !important;
          line-height: 1.15 !important;
        }


        .participation-report-header p {
          font-size: 7px !important;
          margin-top: 2px !important;
        }


        .participation-report-header svg {
          width: 15px !important;
          height: 15px !important;
        }


        .participation-report-content {
          padding: 8px !important;
        }


        .participation-summary-grid {
          display: grid !important;
          grid-template-columns:
            repeat(4, minmax(0, 1fr)) !important;

          gap: 6px !important;
          margin-bottom: 7px !important;
        }


        .participation-summary-grid > div {
          padding: 6px 8px !important;
          border-radius: 7px !important;
        }


        .participation-summary-grid > div > span:first-child {
          font-size: 16px !important;
          line-height: 1 !important;
        }


        .participation-summary-grid > div > span:last-child {
          font-size: 6.5px !important;
          line-height: 1 !important;
        }


        .participation-rate-box {
          margin-bottom: 7px !important;
          padding: 5px 8px !important;
        }


        .participation-rate-box p {
          margin: 0 !important;
        }


        .participation-rate-box p:first-child {
          font-size: 8px !important;
        }


        .participation-rate-box p:last-child {
          font-size: 6.5px !important;
        }


        .participation-rate-box > span {
          font-size: 16px !important;
        }


        /* SINIF KARTLARI */

        .participation-class-grid {
          display: grid !important;

          grid-template-columns:
            repeat(4, minmax(0, 1fr)) !important;

          gap: 6px !important;

          align-items:
            start !important;

          grid-auto-flow:
            dense !important;
        }


        .participation-class-card {
          break-inside:
            avoid !important;

          page-break-inside:
            avoid !important;

          border-radius:
            7px !important;
        }


        /* Çok eksikli sınıflar iki sütun genişliğinde */
        .participation-class-card-wide {
          grid-column:
            span 2 / span 2 !important;
        }


        .participation-class-head {
          padding:
            5px 7px !important;
        }


        .participation-class-head h4 {
          font-size:
            9px !important;

          line-height:
            1.1 !important;
        }


        .participation-class-meta {
          font-size:
            6.5px !important;
        }


        .participation-class-badge {
          font-size:
            6.5px !important;

          padding:
            2px 5px !important;
        }


        .participation-class-card > div:last-child {
          padding:
            5px 7px !important;
        }


        .participation-class-card-wide
        .participation-student-list {

          display:
            grid !important;

          grid-template-columns:
            repeat(2, minmax(0, 1fr)) !important;

          column-gap:
            10px !important;
        }


        .participation-student-row {
          padding:
            2.5px 2px !important;

          min-height:
            0 !important;
        }


        .student-no {
          width:
            37px !important;

          font-size:
            7px !important;
        }


        .student-name {
          font-size:
            7.5px !important;

          line-height:
            1.12 !important;
        }


        .participation-complete-strip {
          margin-top:
            6px !important;

          padding:
            5px 8px !important;

          break-inside:
            avoid !important;

          page-break-inside:
            avoid !important;
        }


        .participation-complete-strip span {
          font-size:
            7px !important;
        }

      `

      : '';


  doc.open();


  doc.write(`

    <!DOCTYPE html>

    <html>

      <head>

        <title>
          Kurumsal Rapor
        </title>

        ${stylesHTML}

        <style>

          @media print {

            @page {
              size: ${pageSize};
              margin: ${pageMargin};
            }


            body {

              font-family:
                'Segoe UI',
                sans-serif;

              -webkit-print-color-adjust:
                exact;

              print-color-adjust:
                exact;

              background:
                white !important;

            }


            .avoid-break {

              page-break-inside:
                avoid !important;

              break-inside:
                avoid !important;

            }


            .bg-slate-900 {

              background-color:
                #0f172a !important;

              color:
                white !important;

            }


            .no-print {
              display:
                none !important;
            }


            ${participationPrintStyles}

          }

        </style>

      </head>


      <body class="bg-white text-black">

        ${content}

      </body>

    </html>

  `);


  doc.close();


  iframe.contentWindow.focus();

  iframe.contentWindow.print();


  setDownloading(false);


  // Mobil tarayıcı PDF'i hazırlarken
  // iframe'i erkenden silme.
  setTimeout(
    () => {

      if (
        document.body.contains(
          iframe
        )
      ) {

        document.body.removeChild(
          iframe
        );

      }

    },
    999000000
  );

};

const generateInstitutionalAI = async (name, items) => { 
    setAiLoading(true); 
    try { 
      let topIssues = "Veri bulunamadı.";
      if (items && Array.isArray(items) && items.length > 0) {
          topIssues = items.slice(0, 15).map(i => {
              if (typeof i === 'string') return i;
              const itemName = i.text || i.name || i.subject || i.style || i.label || i.madde || i.target || i.risk || "Belirsiz Kategori";
              const itemRate = i.percent !== undefined ? i.percent : (i.percentage || i.rate || i.oran || i.aspRounded || i.count || "");
              return `${itemName} ${itemRate ? '(%'+itemRate+')' : ''}`;
          }).join(", ");
      }
      
      const CORPORATE_AI_URL = "https://us-central1-rehber360-e0808.cloudfunctions.net/analyzeCorporateInventory";

      const res = await fetch(CORPORATE_AI_URL, { 
        method: "POST", 
        headers: { "Content-Type": "application/json" }, 
        body: JSON.stringify({ 
          groupName: name, 
          testId: selectedTest, 
          topIssues: topIssues,
          schoolType: schoolType,
          userId: user.uid // 🛡️ KİMLİK KARTI BURADA GİDİYOR
        }) 
      });
      
      if (!res.ok) { 
        // 🚀 YENİ HATA YAKALAYICI: Gelen hatanın ne olduğuna bakıyoruz
        const errData = await res.json().catch(() => ({})); 
        if (errData.error === "INSUFFICIENT_CREDITS") {
            throw new Error("⚠️ Kurumsal analiz için krediniz yetersiz! Lütfen Premium pakete geçin veya kredi yükleyin.");
        }
        throw new Error(errData.error || "Sunucu hatası. Backend'e ulaşılamadı."); 
      } 
      
      const data = await res.json(); 
      setAiResult(data.result); 
      
      // 🚀 Firebase'e Kurumsal Analizi Eğitim Yılı Bazlı Kalıcı Olarak Kaydet
const scopeKey =
  selectedClass
    ? selectedClass
    : 'school';


const safeDocId =
  `${user.uid}_${academicYear}_${selectedTest}_${scopeKey}`
    .replace(/[\/\s]/g, '_');


await setDoc(
  doc(
    db,
    "corporate_analyses",
    safeDocId
  ),
  {
    clinicianUid:
      user.uid,

    academicYear:
      academicYear,

    testId:
      selectedTest,

    className:
      selectedClass || 'school',

    aiAnalysis:
      data.result,

    timestamp:
      new Date().toISOString()
  },
  {
    merge: true
  }
);
      
    } catch (error) { 
      alert(error.message); 
    } finally { 
      setAiLoading(false); 
    } 
  };

  const generateSociometryAI = async (className, socioData) => {
    setAiLoading(true);
    try {
      const prompt = `
        GÖREV: Aşağıdaki SOSYOMETRİ (İlişki Ağı) verilerini analiz et ve Sınıf Rehber Öğretmeni için bir rapor oluştur.
        
        SINIF: ${className}
        VERİLER:
        - Liderler: ${socioData.leaders.map(id => getStudentLabel(id, className)).join(', ') || 'Yok'}
        - Yalnızlar: ${socioData.loners.map(id => getStudentLabel(id, className)).join(', ') || 'Yok'}
        - Karşılıklı Çekim Sayısı: ${socioData.mutualCount}
        - Genel Yorum: ${socioData.text}
        
        İSTENEN RAPOR FORMATI:
        1. GRUP DİNAMİĞİ ANALİZİ
        2. LİDERLİK YAPISI
        3. RİSK GRUBU (YALNIZLAR) İÇİN ÖNERİLER
        4. ÖĞRETMEN İÇİN STRATEJİLER
      `;
      
      const res = await fetch(AI_API_URL, { 
        method: "POST", 
        headers: { "Content-Type": "application/json" }, 
        body: JSON.stringify({ 
          studentName: className, 
          testName: "SOSYOMETRİ ANALİZİ", 
          riskLevel: "GRUP DİNAMİĞİ", 
          markedItems: [prompt],
          userId: user.uid // 🛡️ KİMLİK KARTI BURADA GİDİYOR
        }) 
      }); 
      
      if (!res.ok) { 
        // 🚀 YENİ HATA YAKALAYICI
        const errData = await res.json().catch(() => ({})); 
        if (errData.error === "INSUFFICIENT_CREDITS") {
            throw new Error("⚠️ Sosyometri analizi için krediniz yetersiz! Lütfen Premium pakete geçin.");
        }
        throw new Error(errData.error || "Sunucu hatası. Backend'e ulaşılamadı."); 
      } 
      
      const data = await res.json();
      setAiResult(data.result);

      // 🚀 Firebase'e Sosyometri Analizini Kalıcı Olarak Kaydet
      const scopeKey =
  selectedClass ? selectedClass : 'school';

const safeDocId =
  `${user.uid}_${academicYear}_${selectedTest}_${scopeKey}`
    .replace(/[\/\s]/g, '_');

await setDoc(
  doc(db, "corporate_analyses", safeDocId),
  {
    clinicianUid: user.uid,
    academicYear: academicYear,
    testId: selectedTest,
    className: selectedClass || 'school',
    aiAnalysis: data.result,
    timestamp: new Date().toISOString()
  },
  { merge: true }
);

    } catch (e) { 
      alert(e.message); 
    } finally { 
      setAiLoading(false); 
    }
  };

  const analyzeData = (filterClass = null) => { 
    const subs =
  (mockSubmissions || submissions)
    .filter(
      s =>
        s.testId ===
          selectedTest &&

        s.status ===
          'reported' &&

        (
          !filterClass ||

          isSubmissionInClassForReport(
            s,
            filterClass,
            selectedTest
          )
        )
    );
    const total = subs.length; 
    if (total === 0) return null; 
    const itemCounts = {}; 
    const itemStudents = {}; 
    
    // --- YENİ EKLENEN: ÖĞRENCİ BİLGİ FORMU İÇİN ÖZEL MADDE ANALİZİ ---
    if (selectedTest === 'ogrenci_bilgi_formu') {
      const riskMap = {}; // Benzersiz riskleri tutmak için

      subs.forEach(s => {
        const risks = s.reportData?.risks || [];
        risks.forEach(risk => {
          const riskKey = risk.text; // Örn: "Anne Vefat Etmiş", "Düşük Gelir Düzeyi"
          
          if (!riskMap[riskKey]) {
            riskMap[riskKey] = { id: risk.id || '-', text: risk.text };
          }
          
          itemCounts[riskKey] = (itemCounts[riskKey] || 0) + 1;
          if (!itemStudents[riskKey]) itemStudents[riskKey] = [];
          
          // Öğrenciyi bu riskin listesine ekle
          itemStudents[riskKey].push({
  no:
    s.studentNo,

  class:
    getSubmissionDisplayClass(
      s
    ),

  detail:
    risk.detail
});
        });
      });

      const allItems = Object.entries(itemCounts).map(([key, count]) => {
        return { 
          id: riskMap[key].id, 
          text: riskMap[key].text, 
          count: count, 
          percent: Math.round((count / total) * 100), 
          students: itemStudents[key] || [] 
        };
      }).sort((a,b) => b.count - a.count); // En çok görülenden en aza sırala

      return { total, topItems: allItems.slice(0, 8), criticalItems: [], allItems };
    }
    // ------------------------------------------------------------------

   // ESKİ VE YENİ MANTIK BİRLEŞİMİ (Tüm testleri kapsayan evrensel yapı)
    subs.forEach(s => { 
      if (s.answers) { 
        Object.keys(s.answers).forEach(k => { 
          const val = s.answers[k]; 
          const qId = parseInt(k); 

          // 1. Boş veya işaretlenmemiş (false) yanıtları atla (Yaşam Pencerem uyumluluğu)
          if (val === false || val === "false" || val === null || val === "") return; 

          // 2. Devamsızlık, Bağımlılık gibi sayısal risk testlerinde eski "sadece risklileri say" mantığını koru
          const isRiskBasedTest = selectedTest.includes('devamsizlik') || selectedTest.includes('bagimlilik') || selectedTest.includes('sinav');
          if (isRiskBasedTest && typeof val === 'number' && val < 3) return;

          // 3. Rosenberg, Holland, VAK gibi testlerde (A, B veya Görsel gibi metinler) şıkkı soruya ekle
          let itemKey = k;
          let displayDetail = "";

          if (typeof val === 'string' && val !== "true" && val !== "1" && isNaN(val)) {
              itemKey = `${k}_${val}`; // Örn: 1. Soru A şıkkı -> "1_A"
              displayDetail = `(Seçim: ${val})`;
          } else if (typeof val === 'number' && !isRiskBasedTest) {
              itemKey = `${k}_${val}`;
              displayDetail = `(Puan: ${val})`;
          }

          itemCounts[itemKey] = (itemCounts[itemKey] || 0) + 1; 
          if (!itemStudents[itemKey]) itemStudents[itemKey] = []; 
          // Detayı öğrenci isminin altına basmak için 'detail' olarak yolluyoruz
          itemStudents[itemKey].push({
  no:
    s.studentNo,

  class:
    getSubmissionDisplayClass(
      s
    ),

  detail:
    displayDetail
}); 
        }); 
      } 
    }); 
    
    const allItems = Object.entries(itemCounts).map(([key, v]) => { 
      const qId = parseInt(key.split('_')[0]); // "1_A" içinden sadece 1'i alır
      const qText = AVAILABLE_TESTS.find(t => t.id === selectedTest)?.questions.find(q => q.id === qId)?.text || "Soru metni bulunamadı"; 
      const valSuffix = key.includes('_') ? ` [Yanıt: ${key.split('_')[1]}]` : '';

      return { 
        id: key, 
        text: qText + valSuffix, // Sorunun yanına "[Yanıt: A]" yazar
        count: v, 
        percent: Math.round((v/total)*100), 
        students: itemStudents[key] || [] 
      }; 
    }).sort((a,b) => b.count - a.count); 
    
    const topItems = allItems.slice(0, 8); 
    const criticalItems = allItems.filter(i => CRITICAL_ITEMS_IDS.includes(parseInt(i.id.split('_')[0]))); 
    return { total, topItems, criticalItems, allItems }; 
  };

   
  const isSociometry = selectedTest === 'sosyometri_testi' || AVAILABLE_TESTS.find(t => t.id === selectedTest)?.type === 'sociometry';

  const SchoolReport = () => { 
    // --- 1. SOSYOMETRİ BÖLÜMÜ (MİMARİ YENİLENDİ VE İZOLE EDİLDİ) ---
        if (isSociometry) {
            const filteredSubs = activeSubmissions.filter(s => (s.testId === 'sosyometri_testi' || s.testId === 'sociometry') && s.studentClass === selectedClass);
            
            // SİHİRLİ DOKUNUŞ: Sınıfın gerçek öğrenci listesini (Array) bulup motora gönderiyoruz
            const classObj = allClasses.find(c => c.name === selectedClass);
            const socioData = calculateSociometry(filteredSubs, classObj ? classObj.students : []);
           
              return (
                <SosyometriKurumsalUI
                    selectedClass={selectedClass}
                    subs={filteredSubs}
                    socioData={socioData}
                    aiResult={aiResult}
                    onGenerateAI={generateSociometryAI}
                    aiLoading={aiLoading}
                    handlePrint={handlePrint}
                    downloading={downloading}
                    onBack={() => { setSelectedClass(null); setAiResult(null); }}
                    getStudentLabel={getStudentLabel}
                />
            );
        }
   
        // --- RİBA OKUL SONUÇ ÇİZELGESİ (VİZYON DERGİSİ + EXCEL MOTORU) ---
        if (selectedTest?.startsWith('riba_kurumsal')) {
            const level = selectedTest.includes('ilkokul') ? 'ilkokul' : selectedTest.includes('ortaokul') ? 'ortaokul' : 'lise';
            
            const ribaSubs = activeSubmissions.filter(s => {
                if (!s.testId.includes('riba')) return false;
                if (level === 'ilkokul') return s.testId.includes('ilkokul');
                if (level === 'ortaokul') return s.testId.includes('ortaokul');
                return !s.testId.includes('ilkokul') && !s.testId.includes('ortaokul'); 
            });
            const data = calculateRibaSchoolASP(ribaSubs);

            if (!data || !data.ribaTable || data.ribaTable.length === 0) {
                return <div className="p-8 text-center text-slate-400">Okul geneli ASP hesaplaması için yeterli (Öğrenci, Veli, Öğretmen) verisi bulunamadı.</div>;
            }

            // 🌟 RAM VE BAKANLIK İÇİN SABİT SIRALAMA (EXCEL İÇİN)
            const repLevel = selectedTest.includes('ilkokul') ? 'ilkokul' : selectedTest.includes('ortaokul') ? 'ortaokul' : 'lise';
            let mebOrder = [];
            if (repLevel === 'ilkokul') mebOrder = ['M42', 'M28', 'M27', 'M21', 'M40', 'M02', 'M11', 'M14', 'M07', 'M38', 'M13', 'M35', 'M08', 'M16', 'M19', 'M41', 'M15', 'M32', 'M29', 'M31', 'M26', 'M04', 'M34', 'M12', 'M05', 'M06', 'M01', 'M22', 'M33', 'M24', 'M44', 'M36', 'M09', 'M25', 'M10', 'M18', 'M30', 'M45'];
            else if (repLevel === 'ortaokul') mebOrder = ['M12', 'M30', 'M08', 'M29', 'M07', 'M36', 'M34', 'M15', 'M48', 'M04', 'M42', 'M11', 'M31', 'M13', 'M14', 'M23', 'M32', 'M47', 'M33', 'M16', 'M03', 'M39', 'M19', 'M45', 'M46', 'M06', 'M09', 'M27', 'M10', 'M38', 'M01', 'M35', 'M22', 'M37', 'M41', 'M18', 'M44', 'M05', 'M02'];
            else mebOrder = ['M05', 'M24', 'M08', 'M03', 'M36', 'M01', 'M21', 'M30', 'M09', 'M06', 'M39', 'M10', 'M15', 'M28', 'M29', 'M17', 'M14', 'M26', 'M40', 'M19', 'M23', 'M42', 'M02', 'M34', 'M41', 'M13', 'M11', 'M32', 'M35', 'M22', 'M20', 'M37', 'M16', 'M04', 'M25', 'M31', 'M27', 'M38', 'M43', 'M44'];

            const ramOrderedTable = [...data.ribaTable].sort((a, b) => {
                let indexA = mebOrder.indexOf(a.code); let indexB = mebOrder.indexOf(b.code);
                if (indexA === -1) indexA = 999; if (indexB === -1) indexB = 999;
                return indexA - indexB;
            });

            const classNames = [...new Set(ribaSubs.map(s => s.studentClass).filter(c => c !== 'Öğretmen' && c !== 'Veli'))].sort();
            const classASPs = {};
            classNames.forEach(cls => {
                const subsForClass = ribaSubs.filter(s => s.studentClass === cls || (s.studentNo && s.studentNo.toString().includes('Veli') && s.studentClass === cls));
                const classData = calculateRibaClassASP(subsForClass);
                if (classData && classData.ribaTable) classASPs[cls] = classData.ribaTable;
            });

            // --- KATILIMCI DAĞILIMI HESAPLAMA ---
const teacherCount = ribaSubs.filter(s => s.studentClass === 'Öğretmen').length;
const parentCount = ribaSubs.filter(s => s.studentNo && s.studentNo.toString().includes('Veli')).length;
const studentCount = data.totalParticipants - teacherCount - parentCount;

// 🌟 DERGİ KAPAĞI İÇİN DİNAMİK TEŞHİS MOTORU 🌟
const sortedByAsp = [...data.ribaTable].sort((a, b) => b.asp - a.asp);
            const top1 = sortedByAsp[0] || { target: "Belirsiz", aspRounded: 0 };
            const top2 = sortedByAsp[1] || { target: "Belirsiz", aspRounded: 0 };
            const top5 = sortedByAsp.slice(0, 5);

            // Uzun hedefleri karta sığdırmak için akıllı kısaltıcı
            const getShortName = (text) => {
                if (!text || text === "Belirsiz") return "Genel Rehberlik";
                const words = text.split(' ');
                return words.length > 6 ? words.slice(0, 6).join(' ') + '...' : text;
            };

            const t1Short = getShortName(top1.target);
            const t2Short = getShortName(top2.target);

            // Baskın renklendirme için odak tespiti
            const t = top1.target.toLowerCase();
            let focus = "genel";
            if (t.match(/sınav|başarı|motivasyon|çalışm|zaman|hedef|odak|verimli/)) focus = "akademik";
            else if (t.match(/zorba|öfke|iletişim|çatışma|akran|sosyal|şiddet|kural|saygı/)) focus = "sosyal";
            else if (t.match(/teknoloji|internet|bağımlılık|ekran|oyun|medya/)) focus = "dijital";
            else if (t.match(/meslek|kariyer|yetenek|ilgi|gelecek|öğrenim|üst kurum/)) focus = "kariyer";
            else if (t.match(/aile|anne|baba|veli|sınır|ihmal/)) focus = "aile";
            else if (t.match(/duygu|kaygı|stres|özgüven|benlik|korku|kendini/)) focus = "psikolojik";

            let diagObj = {}; let pdrObj = {}; let stakeObj = {};

            if (focus === "akademik") {
                diagObj = { icon: GraduationCap, color: "text-blue-700", bg: "bg-blue-50 border-blue-200", title: "Akademik Baskı ve İklim Analizi", text: `Kurumun genel akademik atmosferinde "${t1Short}" ihtiyacı kritik seviyededir. Öğrencilerin performans kaygısı yaşadığı ve bu bariyeri aşmak için stratejik rehberliğe ihtiyaç duyduğu görülmektedir.` };
                pdrObj = { icon: Compass, color: "text-indigo-700", bg: "bg-indigo-50 border-indigo-200", title: "Gelecek Yıl PDR Stratejisi", bullets: [`${t1Short} ekseninde BDT temelli veya gelişimsel grup oturumları düzenlenmesi.`, `İkinci hedef olan "${t2Short}" konulu okul geneli farkındalık semineri.`, `Risk haritasında öne çıkan öğrencilerle akademik koçluk süreci.`] };
                stakeObj = { icon: Users, color: "text-teal-700", bg: "bg-teal-50 border-teal-200", title: "Öğretmen ve Veli Görevleri", bullets: [`Öğretmenler: Sadece notları değil, çabayı da takdir eden destekleyici bir dil kullanmalıdır.`, `Veliler: Evdeki akademik baskıyı azaltıp, "${t2Short}" hedefine uygun rutinler oluşturmalıdır.`] };
            } else if (focus === "sosyal") {
                diagObj = { icon: Users, color: "text-rose-700", bg: "bg-rose-50 border-rose-200", title: "Sosyal Uyum ve Akran Dinamikleri", text: `Veriler, okul ikliminde "${t1Short}" sorununun alarm verdiğini göstermektedir. Öğrenciler arası ilişkilerde onarılması gereken sınır ihlalleri ve sosyal kopuşlar mevcuttur.` };
                pdrObj = { icon: ShieldCheck, color: "text-red-700", bg: "bg-red-50 border-red-200", title: "Gelecek Yıl PDR Stratejisi", bullets: [`Öncelikle "${t1Short}" odaklı psiko-eğitim ve akran arabuluculuğu gruplarının oluşturulması.`, `Sosyometri testleriyle yalnızlaşan veya riskli alt grupların erken tespiti.`, `Öfke kontrolü ve çatışma çözme becerileri atölyeleri.`] };
                stakeObj = { icon: Target, color: "text-amber-700", bg: "bg-amber-50 border-amber-200", title: "Öğretmen ve Veli Görevleri", bullets: [`Öğretmenler: Sınıf içi kutuplaşmaları kıracak takım çalışmalarına ağırlık vermelidir.`, `Veliler: "${t2Short}" konusunu ciddiye almalı, çözüm odaklı bir ev içi iletişim benimsemelidir.`] };
            } else if (focus === "kariyer") {
                diagObj = { icon: Briefcase, color: "text-orange-700", bg: "bg-orange-50 border-orange-200", title: "Gelecek Vizyonu ve Hedefsizlik", text: `Öğrencilerin kendi yeteneklerini tanıma ve yönlerini çizme konusunda "${t1Short}" ihtiyacı zirvededir. Kariyer planlamasında ciddi bir kararsızlık yaşanmaktadır.` };
                pdrObj = { icon: Target, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200", title: "Gelecek Yıl PDR Stratejisi", bullets: [`Okul geneli "${t1Short}" temalı meslek tanıtım panelleri ve kariyer günleri organizasyonu.`, `İkinci hedef olan "${t2Short}" kapsamında üst öğrenim kurumu/üniversite gezileri.`, `Holland Mesleki İlgi Envanteri ile öğrencilerin yetenek haritalarının çıkarılması.`] };
                stakeObj = { icon: GraduationCap, color: "text-teal-700", bg: "bg-teal-50 border-teal-200", title: "Öğretmen ve Veli Görevleri", bullets: [`Öğretmenler: Kendi branşlarının iş dünyasındaki karşılıklarını müfredata dahil etmelidir.`, `Veliler: Çocukların meslek seçimlerinde baskısız, ilgi ve yeteneğe dayalı bir rehberlik sunmalıdır.`] };
            } else if (focus === "dijital") {
                diagObj = { icon: Smartphone, color: "text-purple-700", bg: "bg-purple-50 border-purple-200", title: "Dijital Riskler ve Odak Kaybı", text: `Analizler, "${t1Short}" sorununun öğrencilerin günlük işlevselliğini tehdit ettiğini göstermektedir. Teknoloji kullanımında sağlıklı sınırların kaybolduğu görülmektedir.` };
                pdrObj = { icon: BrainCircuit, color: "text-indigo-700", bg: "bg-indigo-50 border-indigo-200", title: "Gelecek Yıl PDR Stratejisi", bullets: [`"${t1Short}" ekseninde tüm kademelere bilinçli internet/sosyal medya kullanımı eğitimleri.`, `Bağımlılık riski taşıyan odak gruplarla dopamin detoksu planlamaları.`, `Gerçek dünyadaki etkileşimi artıracak alternatif okul projelerinin teşvik edilmesi.`] };
                stakeObj = { icon: ShieldAlert, color: "text-blue-700", bg: "bg-blue-50 border-blue-200", title: "Öğretmen ve Veli Görevleri", bullets: [`Veliler: Ev içi teknoloji sözleşmeleri yapmalı ve "ekransız alanlar/saatler" kuralını uygulamalıdır.`, `Öğretmenler: "${t2Short}" hedefini destekleyecek, ekran dışı sınıf içi etkileşimleri artırmalıdır.`] };
            } else if (focus === "psikolojik") {
                diagObj = { icon: HeartPulse, color: "text-fuchsia-700", bg: "bg-fuchsia-50 border-fuchsia-200", title: "Duygusal Dayanıklılık İhtiyacı", text: `Kurumun ruh sağlığı haritasında "${t1Short}" ve "${t2Short}" yoğunlaşmaktadır. Öğrencilerin duygu regülasyonu (kendini sakinleştirme) becerileri zayıflamıştır.` };
                pdrObj = { icon: Brain, color: "text-purple-700", bg: "bg-purple-50 border-purple-200", title: "Gelecek Yıl PDR Stratejisi", bullets: [`"${t1Short}" temelli, psikolojik sağlamlık (resilience) artırıcı önleyici rehberlik programı.`, `Risk grubundaki öğrencilerle yakından bireysel danışma süreçlerinin yürütülmesi.`, `Öz şefkat, stres ve öfke yönetimi gibi konularda küçük atölyeler yapılması.`] };
                stakeObj = { icon: Activity, color: "text-rose-700", bg: "bg-rose-50 border-rose-200", title: "Öğretmen ve Veli Görevleri", bullets: [`Öğretmenler: Sınıftaki ani duygu değişimlerini veya içe kapanmaları yakından izlemelidir.`, `Veliler: Ergenlik krizlerinde yargılamayan, şefkatli bir güvenli alan oluşturmalıdır.`] };
            } else if (focus === "aile") {
                diagObj = { icon: Home, color: "text-pink-700", bg: "bg-pink-50 border-pink-200", title: "Sistemik Aile ve Çevre Etkisi", text: `Okulda yaşanan uyum sorunlarının kökeninde büyük oranda "${t1Short}" dinamiklerinin yattığı tespit edilmiştir. Ev ve okul arasındaki tutarlılık sarsılmıştır.` };
                pdrObj = { icon: HeartPulse, color: "text-rose-700", bg: "bg-rose-50 border-rose-200", title: "Gelecek Yıl PDR Stratejisi", bullets: [`Okul-Aile işbirliğini yeniden inşa etmek için "${t1Short}" temalı Veli Akademileri.`, `Aile içi iletişim sorunu yaşayan öğrencilerle ve velilerle ortak oturumlar.`, `"${t2Short}" ihtiyacını da kapsayan bilgilendirici veli e-bültenleri hazırlanması.`] };
                stakeObj = { icon: Users, color: "text-purple-700", bg: "bg-purple-50 border-purple-200", title: "Öğretmen ve Veli Görevleri", bullets: [`Veliler: Demokratik ebeveyn tutumlarını benimsemeli ve evde tutarlı sınırlar çizmelidir.`, `Öğretmenler: Dezavantajlı aile yapısına sahip çocuklar için okulu kucaklayıcı bir liman yapmalıdır.`] };
            } else {
                diagObj = { icon: Activity, color: "text-slate-700", bg: "bg-slate-50 border-slate-200", title: "Gelişimsel Rehberlik İhtiyacı", text: `Sistematik RİBA analizi, kurumda öncelikli olarak "${t1Short}" alanında desteğe ihtiyaç duyulduğunu göstermektedir. Öğrenciler gelişim dönemi krizleri yaşamaktadır.` };
                pdrObj = { icon: Brain, color: "text-indigo-700", bg: "bg-indigo-50 border-indigo-200", title: "Gelecek Yıl PDR Stratejisi", bullets: [`"${t1Short}" özelinde tüm okul çapında gelişimsel rehberlik bültenlerinin dağıtılması.`, `Öğrenci tanıma teknikleriyle (Bana Kendini Anlat vb.) gizli risk gruplarının tespiti.`, `"${t2Short}" konusuna yönelik sınıf rehberlik etkinliklerinin eksiksiz yürütülmesi.`] };
                stakeObj = { icon: ShieldCheck, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200", title: "Öğretmen ve Veli Görevleri", bullets: [`Öğretmenler: Sınıf içi etkinliklerde "${t2Short}" hedefini düzenli olarak desteklemelidir.`, `Veliler: Okul PDR servisinin belirlediği yıllık plana evde destekleyici yönde eşlik etmelidir.`] };
            }

            // --- EXCEL İNDİRME FONKSİYONU ---
            const exportToExcel = () => {
    // 1. Veri matrisini oluştur
    const header = ["No", "HEDEFLER", ...classNames, "ORTALAMA"];
    const rows = ramOrderedTable.map((row, idx) => {
        const obj = {
            "No": idx + 1,
            "HEDEFLER": row.target
        };
        classNames.forEach(cls => {
            const classTarget = classASPs[cls]?.find(t => t.code === row.code);
            obj[cls] = classTarget ? classTarget.aspRounded : '-';
        });
        obj["ORTALAMA"] = row.aspRounded;
        return obj;
    });

    // 2. Worksheet oluştur
    const worksheet = XLSX.utils.json_to_sheet(rows, { header: header });
    
    // 3. Workbook oluştur
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "RibaOkulSonuc");

    // 4. İndir
    XLSX.writeFile(workbook, "Okul_Riba_Sonuc_Cizelgesi.xlsx");
};

            return (
                <div id="school-report-area" className="space-y-6 print:space-y-4 bg-white print:bg-white">
                    <div className="flex justify-between items-center border-b pb-4 mb-4 no-print px-2">
                        <h2 className="text-xl font-black">RİBA Yıllık Raporu</h2>
                        <div className="flex gap-2">
                            <button onClick={exportToExcel} className="bg-emerald-600 text-white px-4 py-2.5 rounded-xl font-bold shadow-md hover:bg-emerald-700 transition flex items-center text-xs">
                                📊 Resmi Excel'i İndir
                            </button>
                            <button onClick={() => handlePrint('school-report-area')} className="bg-blue-600 text-white px-4 py-2.5 rounded-xl font-bold shadow-md hover:bg-blue-700 transition flex items-center text-xs">
                                🖨️ Dergiyi Yazdır / PDF
                            </button>
                        </div>
                    </div>

                    {/* ================= VİTRİN: RİBA DERGİ KAPAĞI ================= */}
                    <div className="flex flex-col gap-5 print:gap-3 mb-6">
                        
                        <div className="bg-slate-900 text-white p-5 print:p-3 rounded-2xl flex justify-between items-center shadow-sm shrink-0 avoid-break print:bg-white print:text-black print:border-b-2 print:border-slate-900 print:shadow-none">
                            <div className="flex items-center gap-5 print:gap-3">
                                <div className="h-14 w-14 print:h-10 print:w-10 rounded-xl flex items-center justify-center bg-blue-600 print:bg-slate-800">
                                    <Activity className="h-7 w-7 print:h-5 print:w-5 text-white"/>
                                </div>
                                <div>
                                    <h2 className="text-2xl print:text-lg font-black leading-none mb-1">RİBA Hedef ve Gelecek Vizyonu</h2>
                                    <span className="text-xs print:text-[10px] text-slate-400 print:text-slate-600 font-bold tracking-widest uppercase block">Rehberlik İhtiyaçlarını Belirleme Anketi Okul Özeti</span>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <div className="bg-white/5 border border-white/10 px-3 py-2 print:px-2 print:py-1 rounded-xl text-center print:border-slate-200">
                                    <span className="text-xl print:text-lg font-black text-blue-200 print:text-slate-700">{studentCount}</span>
                                    <span className="text-[9px] print:text-[7px] block opacity-70 uppercase font-bold mt-0.5">Öğrenci</span>
                                </div>
                                <div className="bg-white/5 border border-white/10 px-3 py-2 print:px-2 print:py-1 rounded-xl text-center print:border-slate-200">
                                    <span className="text-xl print:text-lg font-black text-amber-200 print:text-slate-700">{parentCount}</span>
                                    <span className="text-[9px] print:text-[7px] block opacity-70 uppercase font-bold mt-0.5">Veli</span>
                                </div>
                                <div className="bg-white/5 border border-white/10 px-3 py-2 print:px-2 print:py-1 rounded-xl text-center print:border-slate-200">
                                    <span className="text-xl print:text-lg font-black text-emerald-200 print:text-slate-700">{teacherCount}</span>
                                    <span className="text-[9px] print:text-[7px] block opacity-70 uppercase font-bold mt-0.5">Öğretmen</span>
                                </div>
                                <div className="bg-blue-500/30 border border-blue-400/30 px-4 py-2 print:px-2 print:py-1 rounded-xl text-center print:border-slate-300">
                                    <span className="text-xl print:text-lg font-black text-white print:text-slate-900">{data.totalParticipants}</span>
                                    <span className="text-[9px] print:text-[7px] block opacity-90 uppercase font-bold mt-0.5">Toplam</span>
                                </div>
                            </div>
                        </div>

                        {/* 2'Lİ KPI KARTLARI */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 print:gap-3 shrink-0 avoid-break">
                            <div className="bg-red-50 p-5 print:p-4 rounded-2xl border border-red-200 print:bg-white print:border-slate-300 flex flex-col justify-center shadow-sm">
                                <div className="flex items-center mb-1.5">
                                    <AlertTriangle className="h-6 w-6 print:h-4 print:w-4 mr-2 text-red-600"/>
                                    <span className="text-sm print:text-[10px] font-black text-red-700 uppercase tracking-widest">1. Öncelikli / Özel Hedef</span>
                                </div>
                                <p className="text-lg print:text-base font-black text-red-900 mt-1 leading-tight">{top1.target}</p>
                                <p className="text-xs print:text-[10px] font-bold text-red-500 mt-1">Sistem Puanı (ASP): {top1.aspRounded}</p>
                            </div>
                            <div className="bg-amber-50 p-5 print:p-4 rounded-2xl border border-amber-200 print:bg-white print:border-slate-300 flex flex-col justify-center shadow-sm">
                                <div className="flex items-center mb-1.5">
                                    <Target className="h-6 w-6 print:h-4 print:w-4 mr-2 text-amber-600"/>
                                    <span className="text-sm print:text-[10px] font-black text-amber-700 uppercase tracking-widest">2. Öncelikli / Yedek Hedef</span>
                                </div>
                                <p className="text-lg print:text-base font-black text-amber-900 mt-1 leading-tight">{top2.target}</p>
                                <p className="text-xs print:text-[10px] font-bold text-amber-500 mt-1">Sistem Puanı (ASP): {top2.aspRounded}</p>
                            </div>
                        </div>

                        {/* MASONRY KARTLARI (TEŞHİS VE PLANLAR) */}
                        <div className="grid grid-cols-2 gap-5 print:gap-3 shrink-0 avoid-break mt-1">
                            <div className={`col-span-1 p-5 print:p-4 rounded-2xl border flex flex-col justify-center shadow-sm print:shadow-none print:min-h-[120px] ${diagObj.bg} print:bg-white print:border-slate-300`}>
                                <h3 className={`font-bold mb-3 print:mb-2 flex items-center text-xs print:text-[10px] uppercase tracking-widest border-b pb-2 print:border-slate-200 ${diagObj.color.replace('text-', 'border-').replace('700', '200')} ${diagObj.color}`}>
                                    <diagObj.icon className="h-5 w-5 print:h-4 print:w-4 mr-2"/> Kurumsal İhtiyaç Teşhisi
                                </h3>
                                <p className={`text-sm print:text-[11px] font-black mb-1.5 print:mb-1 leading-tight ${diagObj.color}`}>{diagObj.title}</p>
                                <p className="text-[12px] print:text-[9.5px] font-medium text-slate-700 leading-snug">{diagObj.text}</p>
                            </div>

                            <div className={`col-span-1 p-5 print:p-4 rounded-2xl border flex flex-col justify-center shadow-sm print:shadow-none print:min-h-[120px] ${pdrObj.bg} print:bg-white print:border-slate-300`}>
                                <h3 className={`font-bold mb-3 print:mb-2 flex items-center text-xs print:text-[10px] uppercase tracking-widest border-b pb-2 print:border-slate-200 ${pdrObj.color.replace('text-', 'border-').replace('700', '200')} ${pdrObj.color}`}>
                                    <pdrObj.icon className="h-5 w-5 print:h-4 print:w-4 mr-2"/> Yeni Yıl PDR Stratejisi
                                </h3>
                                <p className={`text-sm print:text-[11px] font-black mb-1.5 print:mb-1 leading-tight ${pdrObj.color}`}>{pdrObj.title}</p>
                                <ul className="space-y-1.5 print:space-y-1 mt-1">
                                    {pdrObj.bullets.map((b, i) => (
                                        <li key={i} className="flex items-start text-[11px] print:text-[9px] font-medium text-slate-800 leading-snug"><span className={`mr-1.5 font-black ${pdrObj.color}`}>•</span> {b}</li>
                                    ))}
                                </ul>
                            </div>

                            <div className={`col-span-2 p-5 print:p-4 rounded-2xl border flex flex-col justify-center shadow-sm print:shadow-none ${stakeObj.bg} print:bg-white print:border-slate-300`}>
                                <div className={`flex items-center mb-2 print:mb-1.5 border-b pb-2 print:pb-1.5 print:border-slate-200 ${stakeObj.color.replace('text-', 'border-').replace('700', '200')}`}>
                                    <stakeObj.icon className={`h-5 w-5 print:h-4 print:w-4 mr-2 ${stakeObj.color}`}/>
                                    <h3 className={`font-bold text-xs print:text-[10px] uppercase tracking-widest ${stakeObj.color}`}>Kurumsal Paydaş İşbirliği</h3>
                                </div>
                                <p className={`text-sm print:text-[11px] font-black mb-2 print:mb-1.5 leading-tight ${stakeObj.color}`}>{stakeObj.title}</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-4 print:gap-2 mt-1">
                                     {stakeObj.bullets.map((b, i) => (
                                        <div key={i} className="flex items-start text-[12px] print:text-[9.5px] font-medium text-slate-800 leading-snug"><span className={`mr-1.5 font-black ${stakeObj.color}`}>•</span> {b}</div>
                                    ))}
                                </div>
                            </div>

                            <div className="col-span-2 bg-slate-50 p-5 print:p-4 rounded-2xl border border-slate-200 print:bg-white print:border-slate-300 shadow-sm print:shadow-none">
                                <h3 className="font-bold text-slate-800 flex items-center text-xs print:text-[10px] uppercase tracking-widest border-b border-slate-200 pb-2 mb-3">
                                    <BarChart3 className="h-5 w-5 print:h-4 print:w-4 mr-2 text-slate-600"/> Zirvedeki İlk 5 Rehberlik İhtiyacı (ASP Sıralaması)
                                </h3>
                                <div className="space-y-3 print:space-y-2">
                                    {top5.map((item, idx) => (
                                        <div key={idx}>
                                            <div className="flex justify-between items-end mb-1 print:mb-0.5">
                                                <span className="text-xs print:text-[9.5px] font-bold text-slate-700 line-clamp-1 pr-2">{idx+1}. {item.target}</span>
                                                <span className="text-xs print:text-[9.5px] font-black text-slate-900 shrink-0">{item.aspRounded} <span className="text-[9px] print:text-[8px] font-medium text-slate-500">Puan</span></span>
                                            </div>
                                            <div className="h-2.5 print:h-1.5 w-full bg-slate-200 rounded-full overflow-hidden print:border print:border-slate-200">
                                                <div className={`h-full rounded-full print:bg-slate-700 ${idx === 0 ? 'bg-red-500' : idx === 1 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{ width: `${Math.min((item.aspRounded / top1.aspRounded) * 100, 100)}%` }}></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* AI RAPORU */}
<div className="bg-purple-50 p-6 print:p-0 rounded-3xl border border-purple-100 shadow-sm flex flex-col mb-8 print:bg-white print:border-none print:shadow-none avoid-break">
    
    <div className="flex justify-between items-center mb-4 print:mb-3 border-b border-purple-200 print:border-slate-300 pb-3 print:pb-2 shrink-0 flex-col md:flex-row gap-3">
        <h3 className="font-bold text-purple-900 flex items-center text-sm print:hidden uppercase tracking-widest self-start md:self-auto">
            <Sparkles className="h-4 w-4 mr-2 text-purple-600"/> RİBA Stratejik Yapay Zeka Analizi
        </h3>
        <h3 className="hidden print:flex items-center font-bold text-slate-900 text-sm uppercase tracking-widest w-full">
            <Brain className="h-4 w-4 mr-2"/> Kurumsal İhtiyaç ve Stratejik Eylem Planı
        </h3>
        
        {/* YENİ EKLENEN KISIM: AÇILIR MENÜ VE BUTON YAN YANA */}
        <div className="flex items-center gap-2 print:hidden w-full md:w-auto">
            <select 
                value={schoolType} 
                onChange={(e) => setSchoolType(e.target.value)}
                className="bg-white border border-purple-200 text-purple-900 text-xs py-2 px-3 rounded-lg font-bold focus:outline-none focus:border-purple-500 shadow-sm"
            >
                <option value="Anadolu / Genel Lise">Anadolu Lisesi</option>
                <option value="Meslek Lisesi">Meslek Lisesi</option>
                <option value="Fen Lisesi">Fen Lisesi</option>
                <option value="İmam Hatip Lisesi">İmam Hatip Lisesi</option>
            </select>
            <button onClick={() => generateInstitutionalAI("OKUL GENELİ RİBA", top5.slice(0,2))} disabled={aiLoading} className="px-4 py-2 bg-purple-600 text-white rounded-lg font-bold text-xs shadow-sm hover:bg-purple-700 transition flex items-center shrink-0">
                {aiLoading ? <Loader2 className="animate-spin h-3 w-3 mr-2"/> : "Stratejik Rapor Oluştur"}
            </button>
        </div>
        {/* BİTİŞ */}

    </div>
    
    {aiResult && (
        <div className="bg-white p-5 print:p-0 rounded-xl text-slate-700 print:text-black shadow-sm border border-purple-100 print:border-0 print:shadow-none">
            <EditableAIBox text={aiResult} />
        </div>
    )}
</div>
{/* REHBER360 MARKA İMZASI - Şanımızı Belli Edelim */}
<div className="mt-12 mb-4 flex flex-col items-center justify-center opacity-60 print:opacity-100">
    <div className="flex items-center gap-2 mb-1">
        <div className="h-px w-12 bg-gradient-to-r from-transparent to-slate-300"></div>
        <Sparkles className="h-3 w-3 text-purple-600 print:text-slate-400" />
        <span className="text-[10px] font-black tracking-[0.2em] uppercase text-slate-500 print:text-slate-400">
            Rehber360 Analiz Motoru
        </span>
        <div className="h-px w-12 bg-gradient-to-l from-transparent to-slate-300"></div>
    </div>
    <p className="text-[9px] font-medium text-slate-400 text-center italic leading-tight">
        Bu stratejik rapor, Rehber360 Dijital PDR Asistanı tarafından ileri düzey yapay zeka <br className="hidden print:block" /> 
        algoritmaları ve veri analitik sistemleri kullanılarak oluşturulmuştur.
    </p>
</div>
                    {/* İMZA BLOĞU - SADECE YAZDIRILDIĞINDA VE EN ALTTA ŞIK DURUR */}
                    <div className="mt-16 pt-8 grid grid-cols-2 gap-10 text-center avoid-break">
                        <div className="flex flex-col items-center">
                            <span className="text-sm font-black text-slate-900 uppercase tracking-wider mb-1">Psikolojik Danışman</span>
                            <div className="h-16 w-40 border-b-2 border-slate-400 mb-2"></div>
                            <span className="text-[10px] text-slate-500 font-bold uppercase italic">İmza</span>
                        </div>
                        <div className="flex flex-col items-center">
                            <span className="text-sm font-black text-slate-900 uppercase tracking-wider mb-1">Okul Müdürü</span>
                            <div className="h-16 w-40 border-b-2 border-slate-400 mb-2"></div>
                            <span className="text-[10px] text-slate-500 font-bold uppercase italic">Mühür / İmza</span>
                        </div>
                    </div>
                    {/* 40 MADDELİK ESKİ DEV TABLO (EKRANDA GİZLİ VEYA EN ALTTA, ÇIKTIDA TAMAMEN GİZLİ) */}
                    <div className="print:hidden border-t-4 border-slate-900 pt-8 mt-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-black text-xl text-slate-800">Tüm Sınıfların ASP Dağılım Tablosu</h3>
                            <span className="bg-slate-100 text-slate-500 text-xs px-3 py-1 font-bold rounded">Resmi MEB Çizelgesi</span>
                        </div>
                        <div className="overflow-x-auto bg-white mb-8">
                            <table className="w-full text-xs text-left border-collapse border-2 border-slate-400">
                                <thead className="bg-slate-100 text-slate-800">
                                    <tr>
                                        <th className="p-2 w-8 text-center border border-slate-400 font-bold">No</th>
                                        <th className="p-2 border border-slate-400 font-bold">HEDEFLER</th>
                                        {classNames.map(cls => (
                                            <th key={cls} className="p-2 text-center border border-slate-400 font-bold whitespace-nowrap">{cls}</th>
                                        ))}
                                        <th className="p-2 text-center border border-slate-400 font-bold bg-slate-200 w-24">ORTALAMA</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ramOrderedTable.map((row, idx) => (
                                        <tr key={idx} className={`${row.isTopTwo ? 'bg-red-50 text-red-700 font-bold' : 'text-slate-700'} hover:bg-slate-50 transition`}>
                                            <td className="p-2 text-center border border-slate-300 font-medium">{idx + 1}</td>
                                            <td className="p-2 border border-slate-300 min-w-[200px]">
                                                <div className="flex items-center">
                                                    {row.isTopTwo && <AlertTriangle className="h-3 w-3 mr-1 text-red-600 shrink-0" />}
                                                    {row.target}
                                                </div>
                                            </td>
                                            {classNames.map(cls => {
                                                const classTarget = classASPs[cls]?.find(t => t.code === row.code);
                                                const score = classTarget ? classTarget.aspRounded : '-';
                                                return <td key={cls} className="p-2 text-center border border-slate-300">{score}</td>;
                                            })}
                                            <td className="p-2 text-center border border-slate-300 font-bold bg-slate-100">
                                                {row.aspRounded}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            );
        }

// --- YENİ EKLENEN: ÇOKLU ZEKA ENVANTERİ (OKUL RAPORU) ---
        if (selectedTest === 'coklu_zeka_envanteri' || selectedTest?.includes('coklu_zeka')) {
            const subsForTest = activeSubmissions.filter(s => s.testId === selectedTest && s.status === 'reported');
            return (
              <CokluZekaKurumsalUI 
                title="OKUL GENELİ ÇOKLU ZEKA PROFİLİ" 
                subs={subsForTest} 
                aiResult={aiResult} 
                onGenerateAI={generateInstitutionalAI} 
                aiLoading={aiLoading}
                handlePrint={handlePrint}
                downloading={downloading}
              />
            );
        }



        if (selectedTest === 'ogrenci_bilgi_formu') {

    // Kurumsal risk raporunda yalnız işlenmiş kayıtlar kullanılmaya devam eder.
    const subsForTest =
        activeSubmissions.filter(
            s =>
                s.testId === selectedTest &&
                s.status === 'reported'
        );


    // Katılım takibinde ise status ayrımı yapmıyoruz.
    // Böylece boş gönderilmiş / henüz raporlanmamış kayıtları da
    // "hiç doldurmadı" ile karıştırmıyoruz.
    const participationSubs =
        activeSubmissions.filter(
            s =>
                s.testId === selectedTest
        );


    // Simülatörde sahte sınıf listesini,
    // gerçek kullanımda Firebase sınıf listesini kullan.
    const participationClasses =
        mockSubmissions &&
        mockStudentInfoRoster

            ? [mockStudentInfoRoster]

            : allClasses;


    const registeredTotal =
        getRegisteredStudentCount();


    return (
        <StudentInfoKurumsalUI
            title="OKUL GENELİ BİLGİ ÖZETİ"

            subs={subsForTest}

            registeredTotal={
                registeredTotal
            }

            participationSubs={
                participationSubs
            }

            allClasses={
                participationClasses
            }

            filterClass={null}

            handlePrint={
                handlePrint
            }

            downloading={
                downloading
            }
        />
    );
}

// --- YENİ EKLENEN: VAK ÖĞRENME STİLLERİ (OKUL RAPORU) ---
        if (selectedTest === 'vak_inventory' || selectedTest?.includes('vak')) {
            const subsForTest = activeSubmissions.filter(s => s.testId === selectedTest && s.status === 'reported');
            return (
              <VakKurumsalUI 
                title="OKUL GENELİ ÖĞRENME STİLLERİ PROFİLİ" 
                subs={subsForTest} 
                aiResult={aiResult} 
                onGenerateAI={generateInstitutionalAI} 
                aiLoading={aiLoading}
                handlePrint={handlePrint}
                downloading={downloading}
              />
            );
        }

// YENİ EKLENEN: LIEBOWITZ (OKUL RAPORU)
        if (selectedTest === 'liebowitz_sosyal_kaygi' || selectedTest?.includes('liebowitz')) {
            const subsForTest = activeSubmissions.filter(s => s.testId === selectedTest && s.status === 'reported');
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <LiebowitzBulkReportUI 
                  title="KURUMSAL SOSYAL KAYGI VE FOBİ RAPORU" 
                  subs={subsForTest} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }
    // --- YENİ EKLENEN: EĞER TEST YAŞAM PENCEREM İSE DİREKT ÖZEL ARAYÜZÜ ÇAĞIR ---
        if (selectedTest === 'yasam_pencerem' || selectedTest?.includes('yasam')) {
            const subsForTest = activeSubmissions
  .filter(
    s =>
      s.testId === selectedTest &&
      s.status === 'reported'
  )
  .map(s => ({
    ...s,

    studentClass:
      getSubmissionClassForReport(
        s,
        selectedTest
      ) || 'Güncel Sınıf Eşleşmedi'

  }));
            return (
              <YasamPenceremKurumsalUI 
                title="OKUL GENELİ SONUÇ RAPORU" 
                subs={subsForTest} 
                aiResult={aiResult} 
                onGenerateAI={generateInstitutionalAI} 
                aiLoading={aiLoading}
                handlePrint={handlePrint}
                downloading={downloading}
              />
            );
        }

    // YENİ EKLENEN: PSİKOLOJİK SAĞLAMLIK (OKUL RAPORU)
        if (selectedTest === 'kisa_psikolojik_saglamlik' || selectedTest?.includes('saglamlik')) {
            const subsForTest = activeSubmissions.filter(s => s.testId === selectedTest && s.status === 'reported');
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <PsikolojikSaglamlikBulkReportUI 
                  title="KURUMSAL PSİKOLOJİK SAĞLAMLIK RAPORU" 
                  subs={subsForTest} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }

// --- YENİ EKLENEN 4: EĞER TEST DEVAMSIZLIK İSE ÖZEL ARAYÜZÜ ÇAĞIR ---
        if (selectedTest === 'devamsizlik_nedenleri' || selectedTest?.includes('devamsizlik')) {
            const subsForTest = activeSubmissions.filter(s => s.testId === selectedTest && s.status === 'reported' /* Sınıf sekmesindeysek && s.studentClass === selectedClass eklemeyi unutma */);
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <DevamsizlikKurumsalUI 
                  title="KURUMSAL DEVAMSIZLIK RİSK RAPORU" 
                  subs={subsForTest} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }

// --- YENİ EKLENEN 3: EĞER TEST BAŞARISIZLIK NEDENLERİ İSE ÖZEL ARAYÜZÜ ÇAĞIR ---
        if (selectedTest === 'basarisizlik_nedenleri' || selectedTest?.includes('basarisizlik')) {
            const subsForTest = activeSubmissions.filter(s => s.testId === selectedTest && s.status === 'reported' /* Sınıf sekmesindeysek && s.studentClass === selectedClass eklemeyi unutma */);
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <BasarisizlikKurumsalUI 
                  title="KURUMSAL BAŞARISIZLIK NEDENLERİ RAPORU" 
                  subs={subsForTest} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }

// --- YENİ EKLENEN 7: EĞER TEST ROSENBERG İSE ÖZEL ARAYÜZÜ ÇAĞIR ---
        if (selectedTest === 'rosenberg_benlik_saygisi') {
            const subsForTest = activeSubmissions.filter(s => s.testId === selectedTest && s.status === 'reported' /* Sınıf sekmesindeysek && s.studentClass === selectedClass eklemeyi unutma */);
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <RosenbergKurumsalUI 
                  title="KURUMSAL BENLİK SAYGISI (ROSENBERG) RAPORU" 
                  subs={subsForTest} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }

// --- YENİ EKLENEN 8: EĞER TEST HOLLAND İSE ÖZEL ARAYÜZÜ ÇAĞIR ---
        if (selectedTest === 'holland_inventory') {
            const subsForTest = activeSubmissions.filter(s => s.testId === selectedTest && s.status === 'reported' /* Sınıf sekmesindeysek && s.studentClass === selectedClass eklemeyi unutma */);
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <HollandKurumsalUI 
                  title="KURUMSAL KARİYER VE YÖNELİM RAPORU" 
                  subs={subsForTest} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }

        
// --- YENİ EKLENEN 5: EĞER TEST İNTERNET BAĞIMLILIĞI İSE ÖZEL ARAYÜZÜ ÇAĞIR ---
        if (selectedTest === 'internet_bagimliligi' || selectedTest?.includes('bagimlilik')) {
            const subsForTest = activeSubmissions.filter(s => s.testId === selectedTest && s.status === 'reported' /* Sınıf sekmesindeysek && s.studentClass === selectedClass eklemeyi unutma */);
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <InternetKurumsalUI 
                  title="KURUMSAL İNTERNET BAĞIMLILIĞI RAPORU" 
                  subs={subsForTest} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }

// --- YENİ EKLENEN: MESLEKİ DEĞERLER (OKUL RAPORU) ---
        if (selectedTest === 'mesleki_degerler_envanteri' || selectedTest?.includes('degerler')) {
            const subsForTest = activeSubmissions.filter(s => s.testId === selectedTest && s.status === 'reported');
            return (
              <MeslekiDegerlerKurumsalUI 
                title="OKUL GENELİ KARİYER DEĞERLERİ" 
                subs={subsForTest} 
                aiResult={aiResult} 
                onGenerateAI={generateInstitutionalAI} 
                aiLoading={aiLoading}
                handlePrint={handlePrint}
                downloading={downloading}
              />
            );
        }

// --- YENİ EKLENEN 6: EĞER TEST DASS-21 İSE ÖZEL ARAYÜZÜ ÇAĞIR ---
        if (selectedTest === 'dass_21') {
            const subsForTest = activeSubmissions.filter(s => s.testId === selectedTest && s.status === 'reported' /* Sınıf sekmesindeysek && s.studentClass === selectedClass eklemeyi unutma */);
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <Dass21KurumsalUI 
                  title="KURUMSAL PSİKOLOJİK RİSK (DASS-21) RAPORU" 
                  subs={subsForTest} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }

        // --- YENİ EKLENEN 2: EĞER TEST SINAV KAYGISI İSE DİREKT ÖZEL ARAYÜZÜ ÇAĞIR (OKUL GENELİ) ---
        if (selectedTest === 'sinav_kaygisi' || selectedTest?.includes('sinav')) {
            const subsForTest = activeSubmissions.filter(s => s.testId === selectedTest && s.status === 'reported');
            return (
              <SinavKaygisiKurumsalUI 
                title="OKUL GENELİ SINAV KAYGISI RAPORU" 
                subs={subsForTest} 
                aiResult={aiResult} 
                onGenerateAI={generateInstitutionalAI} 
                aiLoading={aiLoading}
                handlePrint={handlePrint}
                downloading={downloading}
              />
            );
        }

        // Yaşam Pencerem, Sınav Kaygısı, Başarısızlık Nedenleri, Devamsızlık Nedenleri, İnternet Bağımlılığı, DASS-21, Holland veya ROSENBERG DEĞİLSE, diğer testler için eski tablo sistemi (varsayılan) devam etsin
        const data = analyzeData(selectedClass); // (Okul Raporu sekmesindeyse parantez içi boştur: analyzeData() )
        if (!data) return <div className="p-8 text-center text-slate-400">Bu test için veri bulunamadı.</div>;
    
    return (
      <div id="school-report-area" className="space-y-8">
        <div className="text-center border-b pb-4 mb-4">
          <h2 className="text-2xl font-black text-slate-900">OKUL GENELİ SONUÇ RAPORU</h2>
          <p className="text-sm text-slate-500">{AVAILABLE_TESTS.find(t=>t.id===selectedTest)?.name}</p>
          <div className="mt-2 inline-block bg-slate-100 px-3 py-1 rounded text-xs font-bold text-slate-600">Toplam Katılım: {data.total} Öğrenci</div>
        </div>
        
       {selectedTest === 'ogrenci_bilgi_formu' ? (
          <StudentInfoDashboard submissions={submissions.filter(s => s.testId === 'ogrenci_bilgi_formu')} filterClass={null} allClasses={allClasses} />
        ) : (
          <>
            {/* 1. ADIM: KURUMSAL KPI KARTLARI (BAROMETRE) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 avoid-break">
              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center">
                <div className="h-12 w-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mr-4"><Users className="h-6 w-6"/></div>
                <div><p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Toplam Katılım</p><p className="text-2xl font-black text-slate-800">{data.total} <span className="text-sm font-bold text-slate-500">Öğrenci</span></p></div>
              </div>
              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center">
                <div className="h-12 w-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center mr-4"><List className="h-6 w-6"/></div>
                <div><p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Toplam İşaretlenen</p><p className="text-2xl font-black text-slate-800">{data.allItems.length} <span className="text-sm font-bold text-slate-500">Farklı Madde</span></p></div>
              </div>
              <div className={`p-5 rounded-2xl border shadow-sm flex items-center ${data.criticalItems.length > 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center mr-4 ${data.criticalItems.length > 0 ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}`}>
                  {data.criticalItems.length > 0 ? <AlertTriangle className="h-6 w-6"/> : <CheckCircle className="h-6 w-6"/>}
                </div>
                <div><p className={`text-xs font-bold uppercase tracking-wider ${data.criticalItems.length > 0 ? 'text-red-500' : 'text-emerald-600'}`}>Acil Kırmızı Alarm</p><p className={`text-2xl font-black ${data.criticalItems.length > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{data.criticalItems.length} <span className="text-sm font-bold opacity-70">Risk Faktörü</span></p></div>
              </div>
            </div>

            {/* 2. ADIM: GÖRSEL DEVRİM (RECHARTS İLE İLK 10 MADDE BAR CHART) */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm mb-6 avoid-break">
              <h3 className="font-bold text-slate-800 mb-6 flex items-center text-sm border-b pb-3 uppercase tracking-wide">
                <TrendingUp className="h-5 w-5 mr-2 text-indigo-600"/> Okul İklimini Etkileyen İlk 10 Faktör
              </h3>
              <div className="h-[350px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.allItems.slice(0, 10).map(item => ({...item, shortName: `Madde ${item.id}`}))} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#e2e8f0" />
                    <XAxis type="number" hide />
                    <YAxis dataKey="shortName" type="category" width={80} tick={{fontSize: 11, fill: '#64748b', fontWeight: 'bold'}} axisLine={false} tickLine={false} />
                    <Tooltip
                      cursor={{fill: '#f8fafc'}}
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const itemData = payload[0].payload;
                          return (
                            <div className="bg-slate-900 text-white p-3 rounded-lg shadow-xl max-w-[250px] text-xs z-50">
                              <p className="font-bold mb-1 text-indigo-300">Madde {itemData.id}</p>
                              <p className="mb-2 leading-relaxed">{itemData.text}</p>
                              <div className="flex justify-between border-t border-slate-700 pt-2">
                                <span>İşaretleyen: <strong className="text-white">{itemData.count} Öğrenci</strong></span>
                                <span>Oran: <strong className="text-white">%{itemData.percent}</strong></span>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={20}>
                      {data.allItems.slice(0, 10).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.count > data.total * 0.5 ? '#ef4444' : (entry.count > data.total * 0.3 ? '#f59e0b' : '#4f46e5')} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* 3. ADIM: KRİTİK ÖĞRENCİ RADARI (ACİL MÜDAHALE LİSTESİ) */}
            {data.criticalItems.length > 0 && (
              <div className="avoid-break bg-red-50 p-6 rounded-2xl border border-red-100 shadow-sm mb-6">
                <h3 className="font-bold text-red-900 mb-4 flex items-center text-sm uppercase tracking-wide"><AlertTriangle className="h-5 w-5 mr-2 text-red-600"/> Acil Müdahale Gerektiren Kritik Bulgular</h3>
                <div className="overflow-hidden rounded-xl border border-red-200/60 bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-red-50/50 text-red-900 border-b border-red-100">
                      <tr><th className="text-left p-3">Kritik İfade</th><th className="text-center p-3 w-24">Oran</th><th className="text-center p-3 w-32">Kişi Sayısı</th></tr>
                    </thead>
                    <tbody className="divide-y divide-red-50">
                      {data.criticalItems.map((item) => (
                        <tr key={item.id} className="hover:bg-red-50/30 transition">
                          <td className="p-3 font-medium text-red-800">{item.text}</td>
                          <td className="p-3 text-center font-black text-red-600">%{item.percent}</td>
                          <td className="p-3 text-center"><span className="bg-red-100 text-red-700 px-3 py-1 rounded-md font-bold">{item.count} Öğrenci</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 4. ADIM: SIFIR FİRE - TÜM MADDELERİN DÖKÜMÜ */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm avoid-break mb-6">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center text-sm border-b pb-3 uppercase tracking-wide">
                <List className="h-5 w-5 mr-2 text-slate-500"/> İşaretlenen Tüm Maddeler ({data.allItems.length})
              </h3>
              <div className="max-h-[400px] overflow-y-auto custom-scrollbar pr-2 print:max-h-none print:overflow-visible">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600 sticky top-0 z-10 print:static">
                    <tr>
                      <th className="text-left p-3 rounded-tl-lg w-16">Madde</th>
                      <th className="text-left p-3">İfade</th>
                      <th className="text-center p-3 w-24">Oran</th>
                      <th className="text-center p-3 w-24 rounded-tr-lg">Kişi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.allItems.map((item, index) => (
                      <tr key={item.id} className="hover:bg-slate-50 transition">
                        <td className="p-3 font-bold text-slate-400">#{item.id}</td>
                        <td className="p-3 font-medium text-slate-700">{item.text}</td>
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <span className="font-bold text-slate-600">%{item.percent}</span>
                          </div>
                        </td>
                        <td className="p-3 text-center font-bold text-slate-600">{item.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* YAPAY ZEKA STRATEJİ MOTORU (ESKİSİ GİBİ DURUYOR, TASARIMI İYİLEŞTİRİLDİ) */}
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-6 rounded-2xl border border-purple-100 shadow-sm avoid-break print:bg-white print:border-none print:p-0">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-purple-900 flex items-center text-sm print:hidden"><Sparkles className="h-5 w-5 mr-2 text-purple-600"/> Yapay Zeka ile Okulu Değerlendir</h3>
                <h3 className="hidden print:block font-bold text-slate-900 text-lg border-b pb-1 mb-2">Okul PDR Servisi Kurumsal Değerlendirme Raporu</h3>
                <button onClick={() => generateInstitutionalAI("TÜM OKUL GENELİ", data.allItems)} disabled={aiLoading} className="px-5 py-2.5 bg-purple-600 text-white rounded-xl font-bold text-xs hover:bg-purple-700 transition shadow-md print:hidden flex items-center">
                  {aiLoading ? <Loader2 className="animate-spin h-4 w-4 mr-2"/> : <Brain className="h-4 w-4 mr-2"/>}
                  {aiLoading ? "Analiz Ediliyor..." : "Raporu Oluştur"}
                </button>
              </div>{aiResult && <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl text-slate-800 shadow-inner border border-purple-100 print:border-0 print:p-0 print:shadow-none print:text-black">
    <EditableAIBox text={aiResult} />
</div>}
            </div>
          </>
        )}
      </div>    
    );          
  };

  const ClassReport = () => { 
   const classes =
  [
    ...new Set(

      activeSubmissions

        .filter(
          s => {

            if (
              selectedTest
                ?.startsWith(
                  'riba_kurumsal'
                )
            ) {

              if (
                !s.testId
                  ?.includes('riba')
              ) {
                return false;
              }


              if (
                selectedTest
                  .includes(
                    'ilkokul'
                  )
              ) {
                return s.testId
                  .includes(
                    'ilkokul'
                  );
              }


              if (
                selectedTest
                  .includes(
                    'ortaokul'
                  )
              ) {
                return s.testId
                  .includes(
                    'ortaokul'
                  );
              }


              return (
                !s.testId
                  .includes(
                    'ilkokul'
                  ) &&

                !s.testId
                  .includes(
                    'ortaokul'
                  )
              );
            }


            return (
              s.testId ===
                selectedTest ||

              (
                selectedTest ===
                  'sosyometri_testi' &&

                s.testId ===
                  'sosyometri_testi'
              )
            );
          }
        )

        .map(
          s =>
            getSubmissionClassForReport(
              s,
              selectedTest
            )
        )

        .filter(
          className =>
            className &&
            className !==
              'Öğretmen' &&
            className !==
              'Veli'
        )
    )
  ]
    .sort(
      (a, b) =>
        String(a)
          .localeCompare(
            String(b),
            'tr',
            {
              numeric: true
            }
          )
    );
    
    if (selectedClass) { 
        const isSociometry = selectedTest === 'sosyometri_testi' || selectedTest === 'sociometry';
        
        // --- 1. SOSYOMETRİ BÖLÜMÜ (MİMARİ YENİLENDİ VE İZOLE EDİLDİ) ---
        if (isSociometry) {
           const filteredSubs = activeSubmissions.filter(s => (s.testId === 'sosyometri_testi' || s.testId === 'sociometry') && s.studentClass === selectedClass);
            
            // SİHİRLİ DOKUNUŞ: Sınıfın gerçek öğrenci listesini (Array) bulup motora gönderiyoruz
            const classObj = allClasses.find(c => c.name === selectedClass);
            const socioData = calculateSociometry(filteredSubs, classObj ? classObj.students : []);
            
            return (
                <SosyometriKurumsalUI
                    selectedClass={selectedClass}
                    subs={filteredSubs}
                    socioData={socioData}
                    aiResult={aiResult}
                    onGenerateAI={generateSociometryAI}
                    aiLoading={aiLoading}
                    handlePrint={handlePrint}
                    downloading={downloading}
                    onBack={() => { setSelectedClass(null); setAiResult(null); }}
                    getStudentLabel={getStudentLabel}
                />
            );
        }
        
        // --- 2. RİBA SINIF SONUÇ ÇİZELGESİ (GÜNCELLENDİ: ÖĞRETMENLER DE DAHİL) ---
        if (selectedTest?.startsWith('riba_kurumsal')) {
            const level = selectedTest.includes('ilkokul') ? 'ilkokul' : selectedTest.includes('ortaokul') ? 'ortaokul' : 'lise';

            const ribaSubs = activeSubmissions.filter(s => {
                if (!s.testId.includes('riba')) return false;
                
                // Kademe Kontrolü
                let isRightLevel = false;
                if (level === 'ilkokul') isRightLevel = s.testId.includes('ilkokul');
                else if (level === 'ortaokul') isRightLevel = s.testId.includes('ortaokul');
                else isRightLevel = !s.testId.includes('ilkokul') && !s.testId.includes('ortaokul');
                
                if (!isRightLevel) return false;

                // Sınıf Kontrolü (Seçili sınıfın öğrencisi, o sınıfın velisi VEYA okulun öğretmeni)
                return s.studentClass === selectedClass || 
                       (s.studentNo && s.studentNo.toString().includes('Veli') && s.studentClass === selectedClass) ||
                       s.studentClass === 'Öğretmen';
            });
            
            const data = calculateRibaClassASP(ribaSubs);

            if (!data || !data.ribaTable || data.ribaTable.length === 0) {
                return (
                    <div className="space-y-4">
                        <button onClick={() => setSelectedClass(null)} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                        <div className="p-8 text-center text-slate-400">Bu sınıf için ASP hesaplamasına yetecek RİBA verisi bulunamadı.</div>
                    </div>
                );
            }

            // --- KATILIMCI DAĞILIMI HESAPLAMA ---
            const teacherCount = ribaSubs.filter(s => s.studentClass === 'Öğretmen').length;
            const parentCount = ribaSubs.filter(s => s.studentNo && s.studentNo.toString().includes('Veli')).length;
            const studentCount = data.totalParticipants - teacherCount - parentCount;
            // 🌟 DERGİ KAPAĞI İÇİN DİNAMİK TEŞHİS MOTORU 🌟
            const sortedByAsp = [...data.ribaTable].sort((a, b) => b.asp - a.asp);
            const top1 = sortedByAsp[0] || { target: "Belirsiz", aspRounded: 0 };
            const top2 = sortedByAsp[1] || { target: "Belirsiz", aspRounded: 0 };
            const top5 = sortedByAsp.slice(0, 5);

            // Uzun hedefleri karta sığdırmak için akıllı kısaltıcı
            const getShortName = (text) => {
                if (!text || text === "Belirsiz") return "Genel Rehberlik";
                const words = text.split(' ');
                return words.length > 6 ? words.slice(0, 6).join(' ') + '...' : text;
            };

            const t1Short = getShortName(top1.target);
            const t2Short = getShortName(top2.target);

            // Baskın renklendirme için odak tespiti
            const t = top1.target.toLowerCase();
            let focus = "genel";
            if (t.match(/sınav|başarı|motivasyon|çalışm|zaman|hedef|odak|verimli/)) focus = "akademik";
            else if (t.match(/zorba|öfke|iletişim|çatışma|akran|sosyal|şiddet|kural|saygı/)) focus = "sosyal";
            else if (t.match(/teknoloji|internet|bağımlılık|ekran|oyun|medya/)) focus = "dijital";
            else if (t.match(/meslek|kariyer|yetenek|ilgi|gelecek|öğrenim|üst kurum/)) focus = "kariyer";
            else if (t.match(/aile|anne|baba|veli|sınır|ihmal/)) focus = "aile";
            else if (t.match(/duygu|kaygı|stres|özgüven|benlik|korku|kendini/)) focus = "psikolojik";

            let diagObj = {}; let pdrObj = {}; let stakeObj = {};

            if (focus === "akademik") {
                diagObj = { icon: GraduationCap, color: "text-blue-700", bg: "bg-blue-50 border-blue-200", title: "Akademik Baskı ve İklim Analizi", text: `Bu sınıfın kendi dinamikleri incelendiğinde "${t1Short}" ihtiyacı alarm vermektedir. Sınıfta genel bir performans kaygısı ve hedefsizlik görülmektedir.` };
                pdrObj = { icon: Compass, color: "text-indigo-700", bg: "bg-indigo-50 border-indigo-200", title: "Sınıfa Özel PDR Stratejisi", bullets: [`Sınıf çapında "${t1Short}" odaklı motivasyon ve çalışma stratejileri grup etkinliği.`, `İkinci hedef olan "${t2Short}" üzerine farkındalık envanteri uygulanması.`, `Sınav kaygısı yaşayan öğrencilerin BDT oturumları için tespit edilmesi.`] };
                stakeObj = { icon: Users, color: "text-teal-700", bg: "bg-teal-50 border-teal-200", title: "Sınıf Öğretmeni ve Veli Rolü", bullets: [`Sınıf Öğretmeni: Derste çocukları notlarla kıyaslamaktan kaçınmalı, çabayı vurgulamalıdır.`, `Veli Grubu: Bir sonraki veli toplantısında "${t2Short}" ihtiyacı açıkça gündem yapılmalıdır.`] };
            } else if (focus === "sosyal") {
                diagObj = { icon: Users, color: "text-rose-700", bg: "bg-rose-50 border-rose-200", title: "Sosyal Uyum ve Akran Dinamikleri", text: `Sınıf içi veriler "${t1Short}" sorununun gruplaşmalara ve dışlanmalara sebep olduğunu, sınıf aidiyetinin zedelendiğini göstermektedir.` };
                pdrObj = { icon: ShieldCheck, color: "text-red-700", bg: "bg-red-50 border-red-200", title: "Sınıfa Özel PDR Stratejisi", bullets: [`Bu şubeye acilen Sosyometri testi uygulanarak kliklerin (grupların) tespit edilmesi.`, `"${t1Short}" odaklı akran zorbalığı/saygı eğitimi yapılması.`, `Sınıf içinde öfke kontrolü ve arabuluculuk oyunları oynatılması.`] };
                stakeObj = { icon: Target, color: "text-amber-700", bg: "bg-amber-50 border-amber-200", title: "Sınıf Öğretmeni ve Veli Rolü", bullets: [`Sınıf Öğretmeni: Kutuplaşmaları kırmak için oturma düzenini ve takım projelerini sık sık değiştirmelidir.`, `Veli Grubu: İkinci hedef olan "${t2Short}" konusunda aile içi iletişimde dikkatli olmaları istenmelidir.`] };
            } else if (focus === "kariyer") {
                diagObj = { icon: Briefcase, color: "text-orange-700", bg: "bg-orange-50 border-orange-200", title: "Gelecek Vizyonu ve Hedefsizlik", text: `Sınıftaki öğrencilerin kariyer vizyonunda "${t1Short}" ihtiyacı zirvededir. Hangi alana yöneleceklerine dair büyük bir kararsızlık yaşanmaktadır.` };
                pdrObj = { icon: Target, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200", title: "Sınıfa Özel PDR Stratejisi", bullets: [`Sınıfa özel Holland Mesleki İlgi Envanteri uygulanıp bireysel yeteneklerin raporlanması.`, `"${t1Short}" kapsamında üst öğrenim kurumu bilgilendirme toplantısı yapılması.`, `İkinci hedef olan "${t2Short}" ekseninde kariyer rehberliği görüşmeleri.`] };
                stakeObj = { icon: GraduationCap, color: "text-teal-700", bg: "bg-teal-50 border-teal-200", title: "Sınıf Öğretmeni ve Veli Rolü", bullets: [`Sınıf Öğretmeni: Kendi eğitim yolculuğunu sınıfla paylaşıp öğrencilere ufuk açmalıdır.`, `Veli Grubu: Yetenek keşfi konusunda baskısız bir ev ortamı sağlamaları için yönlendirilmelidir.`] };
            } else if (focus === "dijital") {
                diagObj = { icon: Smartphone, color: "text-purple-700", bg: "bg-purple-50 border-purple-200", title: "Dijital Riskler ve Odak Kaybı", text: `Sınıf profilinde "${t1Short}" problemi ders içi dikkati ve uyku düzenini tehdit eden boyutlara ulaşmış görünmektedir.` };
                pdrObj = { icon: BrainCircuit, color: "text-indigo-700", bg: "bg-indigo-50 border-indigo-200", title: "Sınıfa Özel PDR Stratejisi", bullets: [`Sınıf bazında "İnternet Bağımlılığı Ölçeği" uygulanarak riskli öğrencilerin süzülmesi.`, `"${t1Short}" tehlikelerine karşı siber güvenlik farkındalık sunumu yapılması.`, `Dopamin detoksu ve alternatif odaklanma etkinlikleri planlanması.`] };
                stakeObj = { icon: ShieldAlert, color: "text-blue-700", bg: "bg-blue-50 border-blue-200", title: "Sınıf Öğretmeni ve Veli Rolü", bullets: [`Sınıf Öğretmeni: Derste ekran kullanımını tetiklemeyecek fiziksel materyallere yönelmelidir.`, `Veli Grubu: İkinci hedef olan "${t2Short}" kapsamında ev içi teknoloji sözleşmeleri yapılmalıdır.`] };
            } else if (focus === "psikolojik") {
                diagObj = { icon: HeartPulse, color: "text-fuchsia-700", bg: "bg-fuchsia-50 border-fuchsia-200", title: "Duygusal Dayanıklılık İhtiyacı", text: `Öğrencilerin ruhsal ihtiyaçlarında "${t1Short}" başlığı çok net hissedilmektedir. Sınıfın stresle başa çıkma ve duygu regülasyonu kapasitesi desteklenmelidir.` };
                pdrObj = { icon: Brain, color: "text-purple-700", bg: "bg-purple-50 border-purple-200", title: "Sınıfa Özel PDR Stratejisi", bullets: [`Sınıftaki içe kapanık veya riskli öğrenciler tespit edilip bireysel danışmaya alınması.`, `"${t1Short}" temelli psikolojik sağlamlık ve öz-şefkat grup etkinliği.`, `İkinci ana hedef olan "${t2Short}" odaklı gelişimsel farkındalık saati.`] };
                stakeObj = { icon: Activity, color: "text-rose-700", bg: "bg-rose-50 border-rose-200", title: "Sınıf Öğretmeni ve Veli Rolü", bullets: [`Sınıf Öğretmeni: Sınıftaki ani davranış/duygu değişimlerini ivedilikle PDR'ye bildirmelidir.`, `Veli Grubu: Ergenlik dönemi çatışmalarında şefkatli bir iletişim diline teşvik edilmelidir.`] };
            } else if (focus === "aile") {
                diagObj = { icon: Home, color: "text-pink-700", bg: "bg-pink-50 border-pink-200", title: "Sistemik Aile ve Çevre Etkisi", text: `Bu sınıftaki akademik ve sosyal sorunların kökeninde ağırlıklı olarak "${t1Short}" ile ilgili ailevi eksikliklerin (iletişim, sınır koyma) yattığı düşünülmektedir.` };
                pdrObj = { icon: HeartPulse, color: "text-rose-700", bg: "bg-rose-50 border-rose-200", title: "Sınıfa Özel PDR Stratejisi", bullets: [`Bu sınıfın velilerine özel "${t1Short}" temalı acil bir bilgilendirme bülteni hazırlanması.`, `Ailesel risk haritasına giren (parçalanmış vb.) öğrencilerle destekleyici görüşmeler.`, `"${t2Short}" ihtiyacını çözmeye yönelik özel veli görüşmeleri organize edilmesi.`] };
                stakeObj = { icon: Users, color: "text-purple-700", bg: "bg-purple-50 border-purple-200", title: "Sınıf Öğretmeni ve Veli Rolü", bullets: [`Sınıf Öğretmeni: Dezavantajlı aile yapısına sahip çocuklar için sınıfı kapsayıcı bir liman yapmalıdır.`, `Veli Grubu: Demokratik ebeveyn tutumları ve okul-ev tutarlılığı konusunda uyarılmalıdır.`] };
            } else {
                diagObj = { icon: Activity, color: "text-slate-700", bg: "bg-slate-50 border-slate-200", title: "Gelişimsel Rehberlik İhtiyacı", text: `Sınıfın genel analizi, öğrencilerin kendi gelişim dönemlerine özgü olarak acil şekilde "${t1Short}" desteğine ihtiyaç duyduğunu göstermektedir.` };
                pdrObj = { icon: Brain, color: "text-indigo-700", bg: "bg-indigo-50 border-indigo-200", title: "Sınıfa Özel PDR Stratejisi", bullets: [`Sınıf rehberlik saatlerinde müfredat dışına çıkıp "${t1Short}" konusunun işlenmesi.`, `Gizli ihtiyaçları keşfetmek için Bana Kendini Anlat veya Öğrenci Bilgi Formu uygulanması.`, `İkinci hedef olan "${t2Short}" konusunda sınıfın farkındalığının artırılması.`] };
                stakeObj = { icon: ShieldCheck, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200", title: "Sınıf Öğretmeni ve Veli Rolü", bullets: [`Sınıf Öğretmeni: Rehberlik derslerini ciddiyetle yürütüp "${t2Short}" vizyonunu desteklemelidir.`, `Veli Grubu: PDR servisinin sınıf için belirlediği çerçeve hedefler konusunda bilgilendirilmelidir.`] };
            }

            const exportToExcel = () => {
                let htmlContent = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"></head><body><table border="1" style="border-collapse: collapse; font-family: Arial, sans-serif; text-align: center; vertical-align: middle;"><thead><tr><th style="background-color: #f1f5f9; font-weight: bold; padding: 6px;">No</th><th style="background-color: #f1f5f9; font-weight: bold; padding: 6px; width: 400px;">HEDEFLER</th><th style="background-color: #f1f5f9; font-weight: bold; padding: 6px; width: 100px;">ORTALAMA</th></tr></thead><tbody>`;
                data.ribaTable.forEach((row, idx) => {
                    const cellStyle = row.isTopTwo ? 'style="background-color: #fee2e2; color: #b91c1c; font-weight: bold; padding: 6px;"' : 'style="color: #334155; padding: 6px;"';
                    const targetStyle = row.isTopTwo ? 'style="background-color: #fee2e2; color: #b91c1c; font-weight: bold; text-align: left; padding: 6px;"' : 'style="text-align: left; color: #334155; padding: 6px;"';
                    htmlContent += `<tr><td ${cellStyle}>${idx + 1}</td><td ${targetStyle}>${row.target}</td><td ${cellStyle}>${row.aspRounded.toString().replace('.', ',')}</td></tr>`;
                });
                htmlContent += `</tbody></table></body></html>`;
                const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel' });
                const link = document.createElement("a");
                link.setAttribute("href", URL.createObjectURL(blob));
                link.setAttribute("download", `${selectedClass}_Riba_Sonuc_Cizelgesi.xls`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            };

            return (
                <div id="class-report-area" className="space-y-6 print:space-y-4 bg-white print:bg-white">
                    <div className="flex justify-between items-center border-b pb-4 mb-4 no-print px-2">
                        <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                        <div className="flex gap-2">
                            <button onClick={exportToExcel} className="bg-emerald-600 text-white px-4 py-2.5 rounded-xl font-bold shadow-md hover:bg-emerald-700 transition flex items-center text-xs">
                                📊 Resmi Excel'i İndir
                            </button>
                            <button onClick={() => handlePrint('class-report-area')} className="bg-blue-600 text-white px-4 py-2.5 rounded-xl font-bold shadow-md hover:bg-blue-700 transition flex items-center text-xs">
                                🖨️ Sınıf Dergisini Yazdır
                            </button>
                        </div>
                    </div>

                    {/* ================= VİTRİN: RİBA DERGİ KAPAĞI ================= */}
                    <div className="flex flex-col gap-5 print:gap-3 mb-6">
                        <div className="bg-slate-900 text-white p-5 print:p-3 rounded-2xl flex justify-between items-center shadow-sm shrink-0 avoid-break print:bg-white print:text-black print:border-b-2 print:border-slate-900 print:shadow-none">
                            <div className="flex items-center gap-5 print:gap-3">
                                <div className="h-14 w-14 print:h-10 print:w-10 rounded-xl flex items-center justify-center bg-blue-600 print:bg-slate-800">
                                    <Activity className="h-7 w-7 print:h-5 print:w-5 text-white"/>
                                </div>
                                <div>
                                    <h2 className="text-2xl print:text-lg font-black leading-none mb-1">{selectedClass} Sınıfı RİBA Raporu</h2>
                                    <span className="text-xs print:text-[10px] text-slate-400 print:text-slate-600 font-bold tracking-widest uppercase block">Öğrenci & Veli Ağırlıklı Ortak Hedefler</span>
                                </div>
                            </div>
                        </div>

                        {/* 2'Lİ KPI KARTLARI */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 print:gap-3 shrink-0 avoid-break">
                            <div className="bg-red-50 p-5 print:p-4 rounded-2xl border border-red-200 print:bg-white print:border-slate-300 flex flex-col justify-center shadow-sm">
                                <div className="flex items-center mb-1.5">
                                    <AlertTriangle className="h-6 w-6 print:h-4 print:w-4 mr-2 text-red-600"/>
                                    <span className="text-sm print:text-[10px] font-black text-red-700 uppercase tracking-widest">1. Öncelikli Hedef / Kırmızı Alarm</span>
                                </div>
                                <p className="text-lg print:text-base font-black text-red-900 mt-1 leading-tight">{top1.target}</p>
                                <p className="text-xs print:text-[10px] font-bold text-red-500 mt-1">Sınıf Puanı (ASP): {top1.aspRounded}</p>
                            </div>
                            <div className="bg-amber-50 p-5 print:p-4 rounded-2xl border border-amber-200 print:bg-white print:border-slate-300 flex flex-col justify-center shadow-sm">
                                <div className="flex items-center mb-1.5">
                                    <Target className="h-6 w-6 print:h-4 print:w-4 mr-2 text-amber-600"/>
                                    <span className="text-sm print:text-[10px] font-black text-amber-700 uppercase tracking-widest">2. Öncelikli Hedef / Sarı Alarm</span>
                                </div>
                                <p className="text-lg print:text-base font-black text-amber-900 mt-1 leading-tight">{top2.target}</p>
                                <p className="text-xs print:text-[10px] font-bold text-amber-500 mt-1">Sınıf Puanı (ASP): {top2.aspRounded}</p>
                            </div>
                        </div>

                        {/* MASONRY KARTLARI (TEŞHİS VE PLANLAR) */}
                        <div className="grid grid-cols-2 gap-5 print:gap-3 shrink-0 avoid-break mt-1">
                            <div className={`col-span-1 p-5 print:p-4 rounded-2xl border flex flex-col justify-center shadow-sm print:shadow-none print:min-h-[120px] ${diagObj.bg} print:bg-white print:border-slate-300`}>
                                <h3 className={`font-bold mb-3 print:mb-2 flex items-center text-xs print:text-[10px] uppercase tracking-widest border-b pb-2 print:border-slate-200 ${diagObj.color.replace('text-', 'border-').replace('700', '200')} ${diagObj.color}`}>
                                    <diagObj.icon className="h-5 w-5 print:h-4 print:w-4 mr-2"/> Sınıf İhtiyaç Teşhisi
                                </h3>
                                <p className={`text-sm print:text-[11px] font-black mb-1.5 print:mb-1 leading-tight ${diagObj.color}`}>{diagObj.title}</p>
                                <p className="text-[12px] print:text-[9.5px] font-medium text-slate-700 leading-snug">{diagObj.text}</p>
                            </div>

                            <div className={`col-span-1 p-5 print:p-4 rounded-2xl border flex flex-col justify-center shadow-sm print:shadow-none print:min-h-[120px] ${pdrObj.bg} print:bg-white print:border-slate-300`}>
                                <h3 className={`font-bold mb-3 print:mb-2 flex items-center text-xs print:text-[10px] uppercase tracking-widest border-b pb-2 print:border-slate-200 ${pdrObj.color.replace('text-', 'border-').replace('700', '200')} ${pdrObj.color}`}>
                                    <pdrObj.icon className="h-5 w-5 print:h-4 print:w-4 mr-2"/> PDR ve Rehberlik Stratejisi
                                </h3>
                                <p className={`text-sm print:text-[11px] font-black mb-1.5 print:mb-1 leading-tight ${pdrObj.color}`}>{pdrObj.title}</p>
                                <ul className="space-y-1.5 print:space-y-1 mt-1">
                                    {pdrObj.bullets.map((b, i) => (
                                        <li key={i} className="flex items-start text-[11px] print:text-[9px] font-medium text-slate-800 leading-snug"><span className={`mr-1.5 font-black ${pdrObj.color}`}>•</span> {b}</li>
                                    ))}
                                </ul>
                            </div>

                            <div className={`col-span-2 p-5 print:p-4 rounded-2xl border flex flex-col justify-center shadow-sm print:shadow-none ${stakeObj.bg} print:bg-white print:border-slate-300`}>
                                <div className={`flex items-center mb-2 print:mb-1.5 border-b pb-2 print:pb-1.5 print:border-slate-200 ${stakeObj.color.replace('text-', 'border-').replace('700', '200')}`}>
                                    <stakeObj.icon className={`h-5 w-5 print:h-4 print:w-4 mr-2 ${stakeObj.color}`}/>
                                    <h3 className={`font-bold text-xs print:text-[10px] uppercase tracking-widest ${stakeObj.color}`}>Sınıf Öğretmeni & Veli Görevleri</h3>
                                </div>
                                <p className={`text-sm print:text-[11px] font-black mb-2 print:mb-1.5 leading-tight ${stakeObj.color}`}>{stakeObj.title}</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-4 print:gap-2 mt-1">
                                     {stakeObj.bullets.map((b, i) => (
                                        <div key={i} className="flex items-start text-[12px] print:text-[9.5px] font-medium text-slate-800 leading-snug"><span className={`mr-1.5 font-black ${stakeObj.color}`}>•</span> {b}</div>
                                    ))}
                                </div>
                            </div>

                            <div className="col-span-2 bg-slate-50 p-5 print:p-4 rounded-2xl border border-slate-200 print:bg-white print:border-slate-300 shadow-sm print:shadow-none">
                                <h3 className="font-bold text-slate-800 flex items-center text-xs print:text-[10px] uppercase tracking-widest border-b border-slate-200 pb-2 mb-3">
                                    <BarChart3 className="h-5 w-5 print:h-4 print:w-4 mr-2 text-slate-600"/> Sınıfı Etkileyen İlk 5 Faktör (ASP)
                                </h3>
                                <div className="space-y-3 print:space-y-2">
                                    {top5.map((item, idx) => (
                                        <div key={idx}>
                                            <div className="flex justify-between items-end mb-1 print:mb-0.5">
                                                <span className="text-xs print:text-[9.5px] font-bold text-slate-700 line-clamp-1 pr-2">{idx+1}. {item.target}</span>
                                                <span className="text-xs print:text-[9.5px] font-black text-slate-900 shrink-0">{item.aspRounded} <span className="text-[9px] print:text-[8px] font-medium text-slate-500">Puan</span></span>
                                            </div>
                                            <div className="h-2.5 print:h-1.5 w-full bg-slate-200 rounded-full overflow-hidden print:border print:border-slate-200">
                                                <div className={`h-full rounded-full print:bg-slate-700 ${idx === 0 ? 'bg-red-500' : idx === 1 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{ width: `${Math.min((item.aspRounded / top1.aspRounded) * 100, 100)}%` }}></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                   {/* AI RAPORU */}
<div className="bg-purple-50 p-6 print:p-0 rounded-3xl border border-purple-100 shadow-sm flex flex-col mb-8 print:bg-white print:border-none print:shadow-none avoid-break">
    
    <div className="flex justify-between items-center mb-4 print:mb-3 border-b border-purple-200 print:border-slate-300 pb-3 print:pb-2 shrink-0 flex-col md:flex-row gap-3">
        <h3 className="font-bold text-purple-900 flex items-center text-sm print:hidden uppercase tracking-widest self-start md:self-auto">
            <Sparkles className="h-4 w-4 mr-2 text-purple-600"/> Sınıf Geneli Yapay Zeka Analizi
        </h3>
        <h3 className="hidden print:flex items-center font-bold text-slate-900 text-sm uppercase tracking-widest w-full">
            <Brain className="h-4 w-4 mr-2"/> Sınıf Risk Analizi ve Eylem Planı
        </h3>
        
        {/* SINIF ÖZELİ: AÇILIR MENÜ VE BUTON YAN YANA */}
        <div className="flex items-center gap-2 print:hidden w-full md:w-auto">
            <select 
                value={schoolType} 
                onChange={(e) => setSchoolType(e.target.value)}
                className="bg-white border border-purple-200 text-purple-900 text-xs py-2 px-3 rounded-lg font-bold focus:outline-none focus:border-purple-500 shadow-sm"
            >
                <option value="Anadolu / Genel Lise">Anadolu Lisesi</option>
                <option value="Meslek Lisesi">Meslek Lisesi</option>
                <option value="Fen Lisesi">Fen Lisesi</option>
                <option value="İmam Hatip Lisesi">İmam Hatip Lisesi</option>
            </select>
            <button onClick={() => generateInstitutionalAI(`${selectedClass} SINIFI RİBA`, top5.slice(0,2))} disabled={aiLoading} className="px-4 py-2 bg-purple-600 text-white rounded-lg font-bold text-xs shadow-sm hover:bg-purple-700 transition flex items-center shrink-0">
                {aiLoading ? <Loader2 className="animate-spin h-3 w-3 mr-2"/> : "Yapay Zeka ile Yorumla"}
            </button>
        </div>
        {/* BİTİŞ */}

    </div>
    
    {aiResult && (
        <div className="bg-white p-5 print:p-0 rounded-xl text-slate-700 print:text-black shadow-sm border border-purple-100 print:border-0 print:shadow-none">
            <EditableAIBox text={aiResult} />
        </div>
    )}
</div>
{/* REHBER360 MARKA İMZASI - Şanımızı Belli Edelim */}
<div className="mt-12 mb-4 flex flex-col items-center justify-center opacity-60 print:opacity-100">
    <div className="flex items-center gap-2 mb-1">
        <div className="h-px w-12 bg-gradient-to-r from-transparent to-slate-300"></div>
        <Sparkles className="h-3 w-3 text-purple-600 print:text-slate-400" />
        <span className="text-[10px] font-black tracking-[0.2em] uppercase text-slate-500 print:text-slate-400">
            Rehber360 Analiz Motoru
        </span>
        <div className="h-px w-12 bg-gradient-to-l from-transparent to-slate-300"></div>
    </div>
    <p className="text-[9px] font-medium text-slate-400 text-center italic leading-tight">
        Bu stratejik rapor, Rehber360 Dijital PDR Asistanı tarafından ileri düzey yapay zeka <br className="hidden print:block" /> 
        algoritmaları ve veri analitik sistemleri kullanılarak oluşturulmuştur.
    </p>
</div>
                    {/* İMZA BLOĞU - SADECE YAZDIRILDIĞINDA VE EN ALTTA ŞIK DURUR */}
                    <div className="mt-16 pt-8 grid grid-cols-2 gap-10 text-center avoid-break">
                        <div className="flex flex-col items-center">
                            <span className="text-sm font-black text-slate-900 uppercase tracking-wider mb-1">Psikolojik Danışman</span>
                            <div className="h-16 w-40 border-b-2 border-slate-400 mb-2"></div>
                            <span className="text-[10px] text-slate-500 font-bold uppercase italic">İmza</span>
                        </div>
                        <div className="flex flex-col items-center">
                            <span className="text-sm font-black text-slate-900 uppercase tracking-wider mb-1">Okul Müdürü</span>
                            <div className="h-16 w-40 border-b-2 border-slate-400 mb-2"></div>
                            <span className="text-[10px] text-slate-500 font-bold uppercase italic">Mühür / İmza</span>
                        </div>
                    </div>
                    {/* 40 MADDELİK ESKİ DEV TABLO (EKRANDA GİZLİ VEYA EN ALTTA, ÇIKTIDA TAMAMEN GİZLİ) */}
                    <div className="print:hidden border-t-4 border-slate-900 pt-8 mt-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-black text-xl text-slate-800">{selectedClass} ASP Dağılım Tablosu</h3>
                            <span className="bg-slate-100 text-slate-500 text-xs px-3 py-1 font-bold rounded">Resmi MEB Çizelgesi</span>
                        </div>
                        <div className="overflow-x-auto bg-white mb-8">
                            <table className="w-full text-xs text-left border-collapse border-2 border-slate-400">
                                <thead className="bg-slate-100 text-slate-800">
                                    <tr>
                                        <th className="p-3 w-12 text-center border border-slate-400 font-bold">No</th>
                                        <th className="p-3 border border-slate-400 font-bold">HEDEFLER</th>
                                        <th className="p-3 text-center w-32 border border-slate-400 font-bold">ORTALAMA (ASP)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.ribaTable.map((row, idx) => (
                                        <tr key={idx} className={`${row.isTopTwo ? 'bg-red-50 text-red-700 font-bold' : 'text-slate-700'} hover:bg-slate-50 transition`}>
                                            <td className="p-2 text-center border border-slate-300 font-medium">{idx + 1}</td>
                                            <td className="p-2 border border-slate-300">
                                                <div className="flex items-center">
                                                    {row.isTopTwo && <AlertTriangle className="h-4 w-4 mr-2 text-red-600 shrink-0" />}
                                                    {row.target}
                                                </div>
                                            </td>
                                            <td className="p-2 text-center border border-slate-300 font-medium">{row.aspRounded}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            );
        }

// --- YENİ EKLENEN: ÇOKLU ZEKA ENVANTERİ (SINIF RAPORU) ---
        if (selectedTest === 'coklu_zeka_envanteri' || selectedTest?.includes('coklu_zeka')) {
            const subsForClass =
  activeSubmissions.filter(
    s =>
      s.testId ===
        selectedTest &&

      s.status ===
        'reported' &&

      isSubmissionInClassForReport(
        s,
        selectedClass,
        selectedTest
      )
  );
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <CokluZekaKurumsalUI 
                  title={`${selectedClass} SINIFI ÇOKLU ZEKA PROFİLİ`} 
                  subs={subsForClass} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }

// --- YENİ EKLENEN: MESLEKİ DEĞERLER (SINIF RAPORU) ---
        if (selectedTest === 'mesleki_degerler_envanteri' || selectedTest?.includes('degerler')) {
            const subsForClass =
  activeSubmissions.filter(
    s =>
      s.testId ===
        selectedTest &&

      s.status ===
        'reported' &&

      isSubmissionInClassForReport(
        s,
        selectedClass,
        selectedTest
      )
  );
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <MeslekiDegerlerKurumsalUI 
                  title={`${selectedClass} Sınıfı Kariyer Değerleri`} 
                  subs={subsForClass} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }

       if (selectedTest === 'ogrenci_bilgi_formu') {

   const subsForClass =
  activeSubmissions.filter(
    s =>
      s.testId ===
        selectedTest &&

      s.status ===
        'reported' &&

      isSubmissionInClassForReport(
        s,
        selectedClass,
        selectedTest
      )
  );


    // Katılım motoru tüm Öğrenci Bilgi Formu kayıtlarını görsün.
    // Hangi sınıfın gösterileceğini filterClass belirleyecek.
    const participationSubs =
        activeSubmissions.filter(
            s =>
                s.testId === selectedTest
        );


    const participationClasses =
        mockSubmissions &&
        mockStudentInfoRoster

            ? [mockStudentInfoRoster]

            : allClasses;


    const registeredTotal =
        getRegisteredStudentCount(
            selectedClass
        );


    return (
      <div>

        <div className="flex justify-start mb-4 no-print">

           <button
             onClick={() =>
                setSelectedClass(null)
             }
             className="text-sm font-bold text-blue-600 flex items-center"
           >
             {'<'} Sınıf Listesine Dön
           </button>

        </div>


        <StudentInfoKurumsalUI

            title={
                `${selectedClass} SINIFI BİLGİ ÖZETİ`
            }

            subs={
                subsForClass
            }

            registeredTotal={
                registeredTotal
            }

            participationSubs={
                participationSubs
            }

            allClasses={
                participationClasses
            }

            filterClass={
                selectedClass
            }

            handlePrint={
                handlePrint
            }

            downloading={
                downloading
            }

        />

      </div>
    );
}

// --- YENİ EKLENEN: VAK ÖĞRENME STİLLERİ (SINIF RAPORU) ---
        if (selectedTest === 'vak_inventory' || selectedTest?.includes('vak')) {
            const subsForClass =
  activeSubmissions.filter(
    s =>
      s.testId ===
        selectedTest &&

      s.status ===
        'reported' &&

      isSubmissionInClassForReport(
        s,
        selectedClass,
        selectedTest
      )
  );
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <VakKurumsalUI 
                  title={`${selectedClass} SINIFI ÖĞRENME STİLLERİ`} 
                  subs={subsForClass} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }

// YENİ EKLENEN: LIEBOWITZ (SINIF RAPORU)
        if (selectedTest === 'liebowitz_sosyal_kaygi' || selectedTest?.includes('liebowitz')) {
            const subsForClass =
  activeSubmissions.filter(
    s =>
      s.testId ===
        selectedTest &&

      s.status ===
        'reported' &&

      isSubmissionInClassForReport(
        s,
        selectedClass,
        selectedTest
      )
  );
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <LiebowitzBulkReportUI 
                  title={`${selectedClass} Sınıfı Sosyal Kaygı Raporu`} 
                  subs={subsForClass} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }

        // --- YENİ EKLENEN: EĞER TEST YAŞAM PENCEREM İSE DİREKT ÖZEL ARAYÜZÜ ÇAĞIR ---
        if (selectedTest === 'yasam_pencerem' || selectedTest?.includes('yasam')) {
        const subsForClass = activeSubmissions
  .filter(
    s =>
      s.testId === selectedTest &&
      s.status === 'reported' &&
      isSubmissionInClassForReport(
        s,
        selectedClass,
        selectedTest
      )
  )
  .map(s => ({
    ...s,
    studentClass: selectedClass
  }));
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <YasamPenceremKurumsalUI 
                  title={`${selectedClass} Sınıfı Sonuç Raporu`} 
                  subs={subsForClass} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }

    // YENİ EKLENEN: PSİKOLOJİK SAĞLAMLIK (SINIF RAPORU)
        if (selectedTest === 'kisa_psikolojik_saglamlik' || selectedTest?.includes('saglamlik')) {
           const subsForClass =
  activeSubmissions.filter(
    s =>
      s.testId ===
        selectedTest &&

      s.status ===
        'reported' &&

      isSubmissionInClassForReport(
        s,
        selectedClass,
        selectedTest
      )
  );
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <PsikolojikSaglamlikBulkReportUI 
                  title={`${selectedClass} Sınıfı Psikolojik Sağlamlık Raporu`} 
                  subs={subsForClass} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }

// --- YENİ EKLENEN 3: BAŞARISIZLIK NEDENLERİ (SINIF RAPORU) ---
        if (selectedTest === 'basarisizlik_nedenleri' || selectedTest?.includes('basarisizlik')) {
            const subsForClass =
  activeSubmissions.filter(
    s =>
      s.testId ===
        selectedTest &&

      s.status ===
        'reported' &&

      isSubmissionInClassForReport(
        s,
        selectedClass,
        selectedTest
      )
  );
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <BasarisizlikKurumsalUI 
                  title={`${selectedClass} Sınıfı Başarısızlık Nedenleri Raporu`} 
                  subs={subsForClass} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }

// --- YENİ EKLENEN 4: DEVAMSIZLIK İSE ÖZEL ARAYÜZÜ ÇAĞIR ---
        if (selectedTest === 'devamsizlik_nedenleri' || selectedTest?.includes('devamsizlik')) {
           const subsForClass =
  activeSubmissions.filter(
    s =>
      s.testId ===
        selectedTest &&

      s.status ===
        'reported' &&

      isSubmissionInClassForReport(
        s,
        selectedClass,
        selectedTest
      )
  );
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <DevamsizlikKurumsalUI 
                  title={`${selectedClass} Sınıfı Devamsızlık Risk Raporu`} 
                  subs={subsForClass} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }

// --- YENİ EKLENEN 7: ROSENBERG İSE ÖZEL ARAYÜZÜ ÇAĞIR ---
        if (selectedTest === 'rosenberg_benlik_saygisi') {
            const subsForClass =
  activeSubmissions.filter(
    s =>
      s.testId ===
        selectedTest &&

      s.status ===
        'reported' &&

      isSubmissionInClassForReport(
        s,
        selectedClass,
        selectedTest
      )
  );
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <RosenbergKurumsalUI 
                  title={`${selectedClass} Sınıfı Benlik Saygısı (Rosenberg) Raporu`} 
                  subs={subsForClass} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }

// --- YENİ EKLENEN 8: HOLLAND İSE ÖZEL ARAYÜZÜ ÇAĞIR ---
        if (selectedTest === 'holland_inventory') {
            const subsForClass =
  activeSubmissions.filter(
    s =>
      s.testId ===
        selectedTest &&

      s.status ===
        'reported' &&

      isSubmissionInClassForReport(
        s,
        selectedClass,
        selectedTest
      )
  );
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <HollandKurumsalUI 
                  title={`${selectedClass} Sınıfı Kariyer ve Yönelim Raporu`} 
                  subs={subsForClass} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }

// --- YENİ EKLENEN 5: İNTERNET BAĞIMLILIĞI İSE ÖZEL ARAYÜZÜ ÇAĞIR ---
        if (selectedTest === 'internet_bagimliligi' || selectedTest?.includes('bagimlilik')) {
           const subsForClass =
  activeSubmissions.filter(
    s =>
      s.testId ===
        selectedTest &&

      s.status ===
        'reported' &&

      isSubmissionInClassForReport(
        s,
        selectedClass,
        selectedTest
      )
  );
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <InternetKurumsalUI 
                  title={`${selectedClass} Sınıfı İnternet Bağımlılığı Raporu`} 
                  subs={subsForClass} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }

// --- YENİ EKLENEN 6: DASS-21 İSE ÖZEL ARAYÜZÜ ÇAĞIR ---
        if (selectedTest === 'dass_21') {
            const subsForClass =
  activeSubmissions.filter(
    s =>
      s.testId ===
        selectedTest &&

      s.status ===
        'reported' &&

      isSubmissionInClassForReport(
        s,
        selectedClass,
        selectedTest
      )
  );
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <Dass21KurumsalUI 
                  title={`${selectedClass} Sınıfı Psikolojik Risk (DASS-21) Raporu`} 
                  subs={subsForClass} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }

        // --- YENİ EKLENEN 2: SINAV KAYGISI ---
        if (selectedTest === 'sinav_kaygisi' || selectedTest?.includes('sinav')) {
            const subsForClass =
  activeSubmissions.filter(
    s =>
      s.testId ===
        selectedTest &&

      s.status ===
        'reported' &&

      isSubmissionInClassForReport(
        s,
        selectedClass,
        selectedTest
      )
  );
            return (
              <div>
                <div className="flex justify-start mb-4 no-print">
                   <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
                </div>
                <SinavKaygisiKurumsalUI 
                  title={`${selectedClass} Sınıfı Sınav Kaygısı Raporu`} 
                  subs={subsForClass} 
                  aiResult={aiResult} 
                  onGenerateAI={generateInstitutionalAI} 
                  aiLoading={aiLoading}
                  handlePrint={handlePrint}
                  downloading={downloading}
                />
              </div>
            );
        }

        // Diğer testler için eski tablo sistemi (varsayılan) devam etsin
       // Yaşam Pencerem, Sınav Kaygısı, Başarısızlık Nedenleri, Devamsızlık Nedenleri, İnternet Bağımlılığı, DASS-21, Holland veya ROSENBERG DEĞİLSE, diğer testler için eski tablo sistemi (varsayılan) devam etsin
        const data = analyzeData(selectedClass); // (Okul Raporu sekmesindeyse parantez içi boştur: analyzeData() )
        if (!data) return <div className="p-8 text-center text-slate-400">Bu test için veri bulunamadı.</div>;


        return (
          <div id="class-report-area" className="space-y-8 print:space-y-6">
            
            <div className="flex justify-between items-center border-b pb-4 mb-4 no-print">
              <button onClick={() => {setSelectedClass(null); setAiResult(null);}} className="text-sm font-bold text-blue-600 flex items-center"><ChevronRight className="rotate-180 h-4 w-4 mr-1"/> Sınıf Listesine Dön</button>
              <h2 className="text-xl font-black">{selectedClass} Sınıfı Raporu</h2>
              <button onClick={() => handlePrint('class-report-area')} className="bg-blue-600 text-white px-4 py-2 rounded text-xs font-bold shadow-md hover:bg-blue-700 transition">Yazdır</button>
            </div>
            
            <div className="text-center mb-6">
               <h2 className="text-2xl font-black text-slate-900">{selectedClass} SINIFI SONUÇ RAPORU</h2>
               <p className="text-sm text-slate-500 font-medium mt-1">Katılım: {data.total} Öğrenci</p>
            </div>

            {selectedTest === 'ogrenci_bilgi_formu' ? (
              <StudentInfoDashboard submissions={submissions.filter(s => s.testId === 'ogrenci_bilgi_formu')} filterClass={selectedClass} allClasses={[]} />
            ) : (
              <>
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm print:shadow-none print:border-slate-300 print:rounded-none">
                    <h3 className="font-bold text-slate-800 mb-4 border-b pb-3 uppercase text-sm tracking-wide flex items-center print:text-black">
                        <List className="h-4 w-4 mr-2 text-blue-500 print:hidden"/> Sınıfın Gündemi (İlk 8 Madde)
                    </h3>
                    <div className="grid md:grid-cols-2 gap-x-12 gap-y-3">
                        {data.topItems.map((i, index) => (
                            <div key={i.id} className="flex justify-between items-center border-b border-slate-100 pb-2 text-sm print:border-slate-200">
                                <span className="text-slate-700 flex items-center print:text-black"><span className="text-slate-400 font-bold mr-2 w-4 print:text-black">{index + 1}.</span> {i.text}</span>
                                <span className="font-black text-blue-600 bg-blue-50 px-2 py-1 rounded-md text-xs print:bg-transparent print:text-black print:p-0">%{i.percent}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {selectedTest === 'yasam_pencerem' && data.criticalItems.length > 0 && (
                  <div className="avoid-break bg-red-50 p-6 rounded-2xl border border-red-100 shadow-sm print:bg-transparent print:shadow-none print:border-none print:p-0 print:mt-6">
                    <h3 className="font-bold text-red-900 mb-4 flex items-center text-sm uppercase tracking-wide print:text-black print:border-b print:pb-2">
                      <AlertTriangle className="h-5 w-5 mr-2 text-red-600 print:hidden"/> Sınıf Risk Analizi (Kritik Maddeler)
                    </h3>
                    <div className="overflow-hidden rounded-xl border border-red-200/60 bg-white print:border-slate-300 print:rounded-none">
                        <table className="w-full text-xs">
                          <thead className="bg-red-50 text-red-900 border-b border-red-200 print:bg-slate-100 print:text-black print:border-slate-300">
                            <tr>
                              <th className="text-left p-4 font-bold">Riskli Madde İfadesi</th>
                              <th className="text-center p-4 font-bold w-32">Risk Oranı</th>
                              <th className="text-center p-4 font-bold w-32">Öğrenci</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-red-100 print:divide-slate-200">
                            {data.criticalItems.map(item => (
                              <tr key={item.id} className="hover:bg-red-50/50 transition print:hover:bg-transparent">
                                <td className="p-4 font-medium text-slate-700 print:text-black">{item.text}</td>
                                <td className="p-4 text-center font-black text-red-600 print:text-black">%{item.percent}</td>
                                <td className="p-4 text-center">
                                    <span className="bg-red-100 text-red-800 font-bold px-3 py-1 rounded-full print:bg-transparent print:text-black print:p-0">{item.count}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                    </div>
                  </div>
                )}

                <div className="mt-8">
                    {!aiResult ? (
                        <button onClick={() => generateInstitutionalAI(`${selectedClass} SINIFI`, data.allItems)} disabled={aiLoading} className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-xl font-bold transition-all shadow-lg shadow-purple-200 flex items-center justify-center mx-auto no-print">
                            {aiLoading ? <Loader2 className="animate-spin h-5 w-5 mr-2"/> : <Brain className="h-5 w-5 mr-2"/>}
                            {aiLoading ? "Yapay Zeka Analiz Ediyor..." : "Yapay Zeka ile Sınıf Raporu Oluştur"}
                        </button>
                    ) : (
                        <>
                            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-8 rounded-3xl border border-purple-100 shadow-sm print:hidden">
                                <div className="flex justify-between items-center mb-6 border-b border-purple-200 pb-4">
                                    <h3 className="font-black text-purple-900 text-lg flex items-center uppercase tracking-wide">
                                        <Sparkles className="h-5 w-5 mr-3 text-purple-600"/> Sınıf Risk ve İhtiyaç Analizi
                                    </h3>
                                    <span className="bg-purple-200 text-purple-800 text-[10px] font-black px-3 py-1 rounded-full tracking-widest uppercase">Gemini AI</span>
                                </div><div className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl text-slate-800 border border-purple-100/50 shadow-inner animate-in fade-in zoom-in-95 duration-500">
                                <EditableAIBox text={aiResult} />
                                </div>
                            </div>

                            <div className="hidden print:block avoid-break mt-6">
                                <h3 className="font-bold text-slate-900 text-lg border-b border-slate-300 pb-2 mb-4 uppercase">
                                   Sınıf Risk ve İhtiyaç Analizi Özeti
                                </h3>
                                <div className="text-black break-words">
                                <EditableAIBox text={aiResult} />
                                </div>
                            </div>
                        </>
                    )}
                </div>
              </>
            )}
          </div>
        );
    }

    // --- 4. SINIF SEÇİLMEDİYSE BUTONLARI (KLASÖRLERİ) GÖSTER ---
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {classes.map(c => (
          <div key={c} onClick={() => setSelectedClass(c)} className="bg-white p-6 rounded-xl border border-slate-200 hover:border-blue-500 cursor-pointer shadow-sm hover:shadow-md transition text-center group">
            <div className="h-12 w-12 bg-blue-50 rounded-full mx-auto mb-3 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition text-blue-500"><Users className="h-6 w-6"/></div>
            <h3 className="font-black text-xl text-slate-800">{c}</h3>
            <p className="text-xs text-slate-400 mt-1 font-medium group-hover:text-blue-500 transition">Raporu Görüntüle</p>
          </div>
        ))}
      </div>
    ); 
  };
  const ItemAnalysisReport = () => { 
    const data = analyzeData(); 
    if (!data) return <div className="p-8 text-center text-slate-400">Veri yok.</div>; 
    return (
      <div className="grid grid-cols-3 gap-6 h-[600px]">
        <div className="col-span-1 border-r pr-4 overflow-y-auto custom-scrollbar">
          <h3 className="font-bold text-slate-800 mb-4 sticky top-0 bg-white pb-2 border-b">Maddeler</h3>
          <div className="space-y-2">
            {data.allItems.map(item => (
              <div key={item.id} onClick={() => setSelectedItemForAnalysis(item)} className={`p-3 rounded-lg cursor-pointer transition text-xs border ${selectedItemForAnalysis?.id === item.id ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-slate-100 hover:bg-slate-50'}`}>
                <div className="flex justify-between items-start"><span className="font-bold mr-2">#{item.id}</span><span className="bg-slate-200 text-slate-600 px-1.5 rounded text-[10px]">{item.count} Kişi</span></div>
                <p className="mt-1 line-clamp-2">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="col-span-2 overflow-y-auto custom-scrollbar bg-slate-50 rounded-xl p-6">
          {selectedItemForAnalysis ? (
            <>
              <div className="mb-6"><span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-1 rounded mb-2 inline-block">Madde #{selectedItemForAnalysis.id}</span><h3 className="text-lg font-black text-slate-800">{selectedItemForAnalysis.text}</h3><p className="text-sm text-slate-500 mt-1">Bu maddeyi işaretleyen <strong>{selectedItemForAnalysis.students.length}</strong> öğrenci bulundu.</p></div>
              <div className="grid grid-cols-2 gap-4">
                {selectedItemForAnalysis.students.map((stu, i) => {
                  const displayName = getStudentLabel(stu.no, stu.class);
                  return (
                    <div key={i} className="bg-white p-3 rounded-lg border border-slate-200 flex flex-col shadow-sm">
                      <div className="flex justify-between items-center w-full">
                        <span className="font-bold text-slate-700">{displayName}</span>
                        <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2 py-1 rounded">{stu.class}</span>
                      </div>
                      {stu.detail && <span className="text-xs font-bold text-red-500 mt-2 bg-red-50 px-2 py-1 rounded-md w-fit border border-red-100">Detay: {stu.detail}</span>}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-slate-400"><List className="h-16 w-16 mb-4 opacity-20"/><p>Öğrenci listesini görmek için soldan bir madde seçin.</p></div>
          )}
        </div>
      </div>
    ); 
  };


  const generateSimulatorData = () => {
        const fakeData = [];

        // 1. RİBA TEST VERİLERİ
        const levels = [
            { name: 'ilkokul', classes: ['1/A', '2/B', '3/A', '4/C'], codes: ['M42', 'M28', 'M27', 'M21', 'M40', 'M02', 'M11', 'M14', 'M07', 'M38', 'M13', 'M35', 'M08', 'M16', 'M19', 'M41', 'M15', 'M32', 'M29', 'M31', 'M26', 'M04', 'M34', 'M12', 'M05', 'M06', 'M01', 'M22', 'M33', 'M24', 'M44', 'M36', 'M09', 'M25', 'M10', 'M18', 'M30', 'M45'] },
            { name: 'ortaokul', classes: ['5/A', '6/B', '7/C', '8/D'], codes: ['M12', 'M30', 'M08', 'M29', 'M07', 'M36', 'M34', 'M15', 'M48', 'M04', 'M42', 'M11', 'M31', 'M13', 'M14', 'M23', 'M32', 'M47', 'M33', 'M16', 'M03', 'M39', 'M19', 'M45', 'M46', 'M06', 'M09', 'M27', 'M10', 'M38', 'M01', 'M35', 'M22', 'M37', 'M41', 'M18', 'M44', 'M05', 'M02'] },
            { name: 'lise', classes: ['9/A', '9/B', '10/A', '11/C'], codes: ['M01', 'M02', 'M03', 'M04', 'M05', 'M06', 'M08', 'M09', 'M10', 'M11', 'M13', 'M14', 'M15', 'M16', 'M17', 'M19', 'M20', 'M21', 'M22', 'M23', 'M24', 'M25', 'M26', 'M27', 'M28', 'M29', 'M30', 'M31', 'M32', 'M34', 'M35', 'M36', 'M37', 'M38', 'M39', 'M40', 'M41', 'M42', 'M43', 'M44'] }
        ];

        levels.forEach(level => {
            for(let i=1; i<=10; i++) {
                const answers = level.codes.filter(() => Math.random() > 0.5).map(code => ({ code }));
                fakeData.push({ id: `ogrt_${level.name}_${i}`, testId: `riba_${level.name}_ogretmen`, testName: `RİBA (${level.name.toUpperCase()} Öğretmen Formu)`, studentClass: 'Öğretmen', studentNo: `Ogrt-${i}`, answers, status: 'reported' });
            }
            level.classes.forEach(cls => {
                for(let i=1; i<=30; i++) {
                    const stuAnswers = level.codes.filter(() => Math.random() > 0.6).map(code => ({ code }));
                    fakeData.push({ id: `stu_${level.name}_${cls}_${i}`, testId: `riba_${level.name}_ogrenci`, testName: `RİBA (${level.name.toUpperCase()} Öğrenci Formu)`, studentClass: cls, studentNo: `${i}`, answers: stuAnswers, status: 'reported' });
                    if(i <= 15) {
                        const parAnswers = level.codes.filter(() => Math.random() > 0.5).map(code => ({ code }));
                        fakeData.push({ id: `par_${level.name}_${cls}_${i}`, testId: `riba_${level.name}_veli`, testName: `RİBA (${level.name.toUpperCase()} Veli Formu)`, studentClass: cls, studentNo: `Veli - ${i}`, answers: parAnswers, status: 'reported' });
                    }
                }
            });
        });

        // 2. SOSYOMETRİ (9/A Sınıfı)
        const sosyoClass = '9/A';
        for (let i = 1; i <= 30; i++) {
            const answers = {};
            if (i === 2 || i === 3 || i === 4) {
                const others = [2, 3, 4].filter(n => n !== i);
                answers['1_1'] = `${others[0]} - Öğrenci ${others[0]}`;
                answers['1_2'] = `${others[1]} - Öğrenci ${others[1]}`;
                answers['1_3'] = `1 - Öğrenci 1`;
            } else if (i === 30) {
                answers['1_1'] = `1 - Öğrenci 1`;
                answers['1_2'] = `10 - Öğrenci 10`;
                answers['1_3'] = `15 - Öğrenci 15`;
            } else {
                answers['1_1'] = Math.random() > 0.3 ? `1 - Öğrenci 1` : `5 - Öğrenci 5`;
                let rand2 = Math.floor(Math.random() * 28) + 2; 
                if (rand2 === i) rand2 = (rand2 % 28) + 2;
                let rand3 = Math.floor(Math.random() * 28) + 2;
                if (rand3 === i || rand3 === rand2) rand3 = (rand3 % 28) + 2;
                answers['1_2'] = `${rand2} - Öğrenci ${rand2}`;
                answers['1_3'] = `${rand3} - Öğrenci ${rand3}`;
            }
            fakeData.push({ id: `sosyo_${sosyoClass}_${i}`, testId: 'sosyometri_testi', testName: 'Sosyometri (Sosyal İlişki Analizi)', studentClass: sosyoClass, studentNo: `${i}`, studentName: `Öğrenci ${i}`, answers, status: 'reported' });
        }

        // 3. BAŞARISIZLIK NEDENLERİ (10/A Sınıfı - Genişletildi)
        const basarisizlikClass = '10/A';
        for (let i = 1; i <= 20; i++) {
            const isHighRisk = i % 3 === 0;
            fakeData.push({
                id: `basarisizlik_${basarisizlikClass}_${i}`,
                testId: 'basarisizlik_nedenleri',
                testName: 'Başarısızlık Nedenleri Anketi', 
                studentClass: basarisizlikClass,
                studentNo: `10${i}`,
                studentName: `Vaka Öğrenci ${i}`,
                answers: isHighRisk ? { "1": true, "2": true, "10": true, "39": true, "14": true, "4": true } : { "1": true, "10": true }, 
                status: 'reported',
                date: new Date().toISOString(),
                reportData: {
                    summary: isHighRisk ? 'YÜKSEK RİSK (KIRMIZI ALARM)' : 'ORTA DÜZEY RİSK',
                    risks: isHighRisk ? [
                        { id: 10, text: "Dikkatimi devamlı olarak derse veremiyorum.", category: "Bilişsel Faktörler ve Sınav Kaygısı" },
                        { id: 14, text: "Öğretmenimin anlattıklarını anlamıyorum.", category: "Okul ve Eğitim Ortamı" },
                        { id: 39, text: "Ders çalışmayı sevmiyorum.", category: "Çalışma Alışkanlıkları ve Motivasyon" },
                        { id: 4,  text: "Ayrı bir çalışma odam yok.", category: "Aile ve Çevre Faktörü", isCritical: true }
                    ] : [
                        { id: 10, text: "Dikkatimi devamlı olarak derse veremiyorum.", category: "Bilişsel Faktörler ve Sınav Kaygısı" }
                    ]
                }
            });
        }

        // 4. DEVAMSIZLIK NEDENLERİ (10/B Sınıfı - Genişletildi)
        const devamsizlikClass = '10/B';
        for (let i = 1; i <= 20; i++) {
            const isKritik = i % 4 === 0;
            fakeData.push({
                id: `devamsizlik_${devamsizlikClass}_${i}`,
                testId: 'devamsizlik_nedenleri',
                testName: 'Devamsızlık Nedenleri Anketi',
                studentClass: devamsizlikClass,
                studentNo: `20${i}`,
                studentName: `Devamsızlık Vakası ${i}`,
                answers: isKritik ? { "1": 3, "12": 3, "23": 3, "44": 3, "49": 3 } : { "1": 3, "12": 3 }, 
                status: 'reported',
                date: new Date().toISOString(),
                reportData: {
                    summary: isKritik ? 'YÜKSEK RİSK (KIRMIZI ALARM)' : 'ORTA RİSK',
                    risks: isKritik ? [
                        { id: 12, text: "Bazı dersler çok sıkıcı geçtiği için", category: "Okul ve Uyum" },
                        { id: 44, text: "Okuldan kaçmak eğlenceli geldiği için", category: "Kişisel Motivasyon" },
                        { id: 49, text: "Okulda arkadaşlarım tarafından dışlandığım için", category: "Okul ve Uyum", isCritical: true },
                        { id: 23, text: "Okul dışında bir işte çalışmak zorunda olduğum için", category: "Aile ve Çevre", isCritical: true }
                    ] : [
                        { id: 12, text: "Bazı dersler çok sıkıcı geçtiği için", category: "Okul ve Uyum" }
                    ]
                }
            });
        }

        // 5. YENİ EKLENEN: ROSENBERG BENLİK SAYGISI (11/C Sınıfı)
        const rosenbergClass = '11/C';
        for (let i = 1; i <= 25; i++) {
            const isLowEsteem = i % 5 === 0;
            const isFragile = i % 2 === 0;
            fakeData.push({
                id: `rosenberg_${rosenbergClass}_${i}`,
                testId: 'rosenberg_benlik_saygisi',
                testName: 'Rosenberg Benlik Saygısı Ölçeği',
                studentClass: rosenbergClass,
                studentNo: `30${i}`,
                studentName: `Rosenberg Vaka ${i}`,
                answers: { "1": "A" }, // Dummy answers since we are directly supplying the reportData below
                status: 'reported',
                date: new Date().toISOString(),
                reportData: {
                    summary: isLowEsteem ? 'DÜŞÜK BENLİK SAYGISI (KIRMIZI ALARM)' : 'DENGELİ (NORMAL SEVİYE)',
                    scores: {
                        D1: isLowEsteem ? 5 : 1, // D1 skoru 0-1 yüksek, 5-6 düşüktür
                        D2: isLowEsteem ? 4 : 1,
                        D3: isFragile ? 3 : 1,
                        D4: isLowEsteem ? 2 : 0,
                        D5: isLowEsteem ? 4 : 1,
                        D6: 1,
                        D7: isFragile ? 6 : 2,
                        D8: isLowEsteem ? 2 : 0,
                        D9: 1, D10: isLowEsteem ? 4 : 1, D11: 2, D12: isLowEsteem ? 1 : 0
                    }
                }
            });
        }

        // 6. ÖĞRENCİ BİLGİ FORMU TEST VERİLERİ
// Tamamen izole simülasyon: gerçek öğrenci/sınıf verisi kullanılmaz.

const bilgiClass = '9/B';

// Sınıf mevcudu 32, formu dolduran 27 öğrenci olsun.
const simulatorClassSize = 32;
const bilgiFormuAdedi = 27;

// Tamamen sahte öğrenci listesi
const simulatorStudents = Array.from(
    { length: simulatorClassSize },
    (_, index) => ({
        no: String(9001 + index),
        name: `TEST ÖĞRENCİ ${String(index + 1).padStart(2, '0')}`
    })
);

// Katılım hesabının kullanacağı sahte sınıf listesi
setMockStudentInfoRoster({
    name: bilgiClass,
    students: simulatorStudents
});

const hastaliklar = [
    "Astım",
    "Tip 1 Diyabet",
    "Kalp Ritmi Bozukluğu",
    "Epilepsi",
    "Migren",
    "Çölyak"
];

for (let i = 1; i <= bilgiFormuAdedi; i++) {

    const student = simulatorStudents[i - 1];

    const hasRisk = i % 4 === 0;
    const isOrphan = i === 5 || i === 12;
    const isWorking = i === 8;
    const hasChronic = i % 5 === 0;
    const hasMedication = hasChronic && i % 10 === 0;
    const hasOperation = i === 14 || i === 22;

    const rawAnswers = {
        5: hasRisk ? "Akraba" : "Anne-Baba",
        6: isOrphan ? "Baba Vefat" : "Sag",
        7: hasRisk ? "Ayri" : "Birlikte",
        8: hasRisk ? "5+" : "2",
        9: hasRisk ? "Dusuk" : "Orta",
        10: hasRisk ? "Kira" : "Mulkiyet",
        11: isWorking ? "Evet" : "Hayir",

        160: hasChronic ? "Evet" : "Hayır",
        16: hasChronic
            ? hastaliklar[(i - 1) % hastaliklar.length]
            : "",

        170: hasMedication ? "Evet" : "Hayır",
        17: hasMedication
            ? "Düzenli ilaç kullanıyor"
            : "",

        180: hasOperation ? "Evet" : "Hayır",
        18: hasOperation
            ? "Geçmiş ameliyat / kaza öyküsü"
            : "",

        19: hasRisk ? "Yuru" : "Servis"
    };

    const reportData = calculateStudentInfo(rawAnswers);

    fakeData.push({
        id: `bilgi_${bilgiClass}_${student.no}`,
        testId: 'ogrenci_bilgi_formu',
        testName: 'Öğrenci Bilgi Formu',
        studentClass: bilgiClass,
        studentNo: student.no,
        studentName: student.name,
        answers: rawAnswers,
        status: 'reported',
        date: new Date().toISOString(),
        createdAt: Date.now() + i,
        reportData
    });
}

        setMockSubmissions(fakeData);
        alert(`SİMÜLASYON BAŞARILI VE ZENGİNLEŞTİRİLDİ!\n\nSisteme anında;\n- 570 Adet RİBA Verisi\n- 30 Kişilik Sosyometri Sınıfı\n- 20 Adet Başarısızlık Vakası (10/A)\n- 20 Adet Devamsızlık Vakası (10/B)\n- 25 Adet Rosenberg Benlik Vakası (11/C)\n- 30 Adet Öğrenci Bilgi Formu (9/B)\n\nbaşarıyla pompalandı!`);
    };

  const clearSimulator = () => {
    setMockSubmissions(null);
    setMockStudentInfoRoster(null);
};
    
    // Uygulamanın geri kalanı artık gerçek 'submissions' yerine 'activeSubmissions' kullanacak
    const activeSubmissions = mockSubmissions || submissions;

const corporateTests = testOptions.filter(t => !t.id.includes('riba'));
corporateTests.push(

    { id: 'riba_kurumsal_ilkokul', name: 'RİBA - İLKOKUL (Sınıf ve Okul Sonuç Çizelgeleri)' },
    { id: 'riba_kurumsal_ortaokul', name: 'RİBA - ORTAOKUL (Sınıf ve Okul Sonuç Çizelgeleri)' },
    { id: 'riba_kurumsal_lise', name: 'RİBA - LİSE (Sınıf ve Okul Sonuç Çizelgeleri)' }
);

  return (
    <div className="space-y-8 animate-in fade-in duration-500 relative">
      <PrintStyles /><div className="bg-slate-900 text-white p-8 rounded-[2.5rem] shadow-2xl flex flex-col md:flex-row justify-between items-center gap-6 no-print">
        
        {/* SOL TARAF: BAŞLIK VE SİMÜLATÖR BUTONLARI */}
        <div>
           <h2 className="text-3xl font-black tracking-tight">Kurumsal Analiz</h2>
           <p className="text-slate-400 mt-2">Okul ve sınıf bazlı detaylı raporlama.</p>
           
           <div className="mt-4 flex gap-2">
               {!mockSubmissions ? (
                   <button onClick={generateSimulatorData} className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs px-3 py-1.5 rounded-lg font-bold transition">🧪 Test Verisi Üret</button>
               ) : (
                   <button onClick={clearSimulator} className="bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1.5 rounded-lg font-bold transition">🧹 Testi Temizle (Gerçeğe Dön)</button>
               )}
           </div>
        </div>

        {/* SAĞ TARAF: AÇILIR MENÜ */}
        <select value={selectedTest} onChange={(e) => { setSelectedTest(e.target.value); setSelectedClass(null); setAiResult(null); }} className="bg-slate-800 border border-slate-700 text-white py-3 px-6 rounded-xl font-bold focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer">
          <option value="">Analiz Edilecek Testi Seçin</option>
          {corporateTests.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>

      </div>
      {selectedTest ? (
        <div className="bg-white rounded-[2rem] border border-slate-200 overflow-hidden shadow-sm min-h-[600px]">
          <div className="flex border-b border-slate-200 no-print">
            <button onClick={() => {setActiveTab('school'); setAiResult(null);}} className={`flex-1 py-4 font-bold text-sm flex items-center justify-center transition ${activeTab === 'school' ? 'bg-slate-50 text-blue-600 border-b-2 border-blue-600' : 'text-slate-400 hover:bg-slate-50'}`}><School className="h-4 w-4 mr-2"/> Okul Raporu</button>
            <button onClick={() => {setActiveTab('class'); setAiResult(null);}} className={`flex-1 py-4 font-bold text-sm flex items-center justify-center transition ${activeTab === 'class' ? 'bg-slate-50 text-blue-600 border-b-2 border-blue-600' : 'text-slate-400 hover:bg-slate-50'}`}><Users className="h-4 w-4 mr-2"/> Sınıf Raporları</button>
            <button onClick={() => {setActiveTab('item'); setAiResult(null);}} className={`flex-1 py-4 font-bold text-sm flex items-center justify-center transition ${activeTab === 'item' ? 'bg-slate-50 text-blue-600 border-b-2 border-blue-600' : 'text-slate-400 hover:bg-slate-50'}`}><List className="h-4 w-4 mr-2"/> Madde Analizi</button>
          </div>
          <div className="p-8">{activeTab === 'school' && (
  <div>
    <SchoolReport />
  </div>
)}
            {activeTab === 'class' && <ClassReport />}
            {activeTab === 'item' && <ItemAnalysisReport />}
          </div>
        </div>
      ) : (
        <div className="bg-slate-50 border-4 border-dashed border-slate-200 rounded-[3rem] h-96 flex flex-col items-center justify-center text-slate-400 no-print"><PieChartIcon className="h-24 w-24 mb-6 opacity-20"/><p className="font-bold text-xl">Lütfen Bir Test Seçiniz</p></div>
      )}
    </div>
  );
};

export default InstitutionalAnalysis;